/**
 * TaskDashboard Component
 * Main dashboard for managing tasks with kanban board view
 * Handles task creation, editing, deletion, and viewing
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { DEFAULT_FILTERS, fetchTicketDropdowns, fetchProjectDropdownsByUser, fetchLastSelectedProject, saveUserPreference, createTicketStatus, createTicketType, createTicketPriority, createTicketLabel, createTicketSprint, updateTicketDropdown, deleteTicketDropdown, fetchTicketsByProject, fetchTicketsByColumn, createTicket, updateTicket, updateTicketPartial, deleteTicket, clearTicketDetails, fetchTicketDetails, fetchTicketComments, fetchWorklogSummary, fetchTicketHistory, setFilters, initializeColumnPagination, updateTicketStatus, updateStatusOrder, setDraggedStatus, reorderStatuses, getDraggedStatus, bulkUpdateTickets, bulkDeleteTickets, linkTickets, searchTickets, deleteLinkTickets, updateLinkTickets, markSubtaskUpdated, setProjectDropdownStatus, optimisticMergeTicket } from '../../store/ticketSlice';
import { fetchUnreadCount } from '../../store/notificationSlice';
import { addCalendarTicket, updateGanttTicket } from '../../store/calendarSlice';
import { updateProject } from '../../store/projectSlice';
import { fetchAndFormatTicketForEdit } from './TaskViewModal';
import { COLORS, UI, getColumnTheme } from '../../shared/colors';
import { handleCopyTaskLink, handleCopyTaskTitle } from '../../shared/taskUtils';
import { ICONS } from '../../shared/icons';
import { SIZING } from '../../shared/globalSX';
import { createDragImage, createAutoScroll, createBoardColumnDragHandlers, createDragVisualManager } from '../../shared/dragUtils';
import { minutesToTimeString, parseBackendDate, getLocalDatePart, getLocalTimePart, isDueDateOverdue, formatDateWithOptionalTime, hasEffectiveDueTime } from '../../shared/timeUtils';
import { createNotificationSoundManager, enableAudio } from '../../shared/audioUtils';
import { normalizeSprintIds } from '../../shared/scrumSprintUtils';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Tooltip,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from '@mui/material';
import {
  Add,
  Timeline,
  AddCircleOutline,
  Flag,
  CheckCircle,
  PauseCircle,
  RemoveCircleOutline,
  Schedule,
  PlayArrow,
  RateReview,
  ViewKanban,
  FormatListBulleted,
  CalendarMonth,
  MoreVert,
  CheckBox,
  CheckBoxOutlineBlank,
  PushPin,
  KeyboardArrowDown,
  Fullscreen,
  FullscreenExit,
  SwapHoriz,
  Close,
  InfoOutlined,
  ContentCopy,
} from '@mui/icons-material';

import Navbar from '../shared/Navbar';
import HeaderProfileMenu from '../shared/HeaderProfileMenu';
import OverflowTooltip from '../../shared/OverflowTooltip';
import AddTaskModal from './AddTaskModal';
import EditTaskModal from './EditTaskModal';
import CommonDeleteConfirmDialog from '../shared/CommonDeleteConfirmDialog';
import TaskViewModal from './TaskViewModal';
import AddBoardColumnModal from './AddBoardColumnModal';
import EditBoardColumnModal from './EditBoardColumnModal';
import DeleteBoardColumnModal from './DeleteBoardColumnModal';
import BulkEditModal from './BulkEditModal';
import BulkDeleteModal from './BulkDeleteModal';
import InlineSubtaskList from './InlineSubtaskList';
import TaskListView from './TaskListView';
import { BoardViewSkeleton, ListViewSkeleton } from './components/SkeletonLoaders';
import TaskCompletionCheckbox from './TaskCompletionCheckbox';
import { getStatusIcon } from '../../shared/iconUtils';
import { CalendarView } from './calendar-view';
import { GanttView } from './gantt-view';
import LabelChips from './LabelChips';
import LinkTicketModal from './LinkTicketModal';
import ViewLinkedTicketsModal from './ViewLinkedTicketsModal';
import KebabMenu, { useMenuState } from '../shared/KebabMenu';
import ProjectStatusModal from './ProjectStatusModal';
import { FILTER_VALUES, SCRUM_BACKLOG_SENTINEL, USER_ROLES, TOAST_MESSAGES, NOTIFICATION_BADGE_THRESHOLDS, PROJECT_STATUS, PROJECT_STATUS_MODAL_CONFIG, PROJECT_TYPES, TIME_FORMATS, VIEW_MODES } from '../../shared/appConstants';
import { BULK_ACTION_ITEMS } from '../../shared/appConstants';

import bellIcon from '../../assets/images/bell_icon.svg';
import refreshIcon from '../../assets/images/refresh_icon.svg';
import AssigneeAvatarStack from '../../shared/AssigneeAvatarStack';
import FilterHeader from './FilterHeader';
import useSavedFilters from './useSavedFilters';
import { getUnavailableSavedFilterFields } from '../../shared/savedFilterUtils';
import { api, ENDPOINTS } from '../../services';
import { getPriorityColor } from '../../shared/commonFunctions';
import './TaskDashboard.scss';

const TaskDashboard = () => {
  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();
  const { dropdowns, dropdownsLoading, tickets, ticketsLoading, filters, ticketDetails, projectDropdownsByUser, projectDropdownsByUserLoading, columnPagination, lastSelectedProjectId } = useSelector(state => state.tickets);
  const draggedStatus = useSelector(getDraggedStatus);
  const { user } = useSelector(state => state.auth);
  const { unreadCount, unreadCountByProject } = useSelector(state => state.notifications);

  // Check if user can reorder status columns (only admin and pm)
  const canReorderStatus = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;

  // Check if user can manage status columns (only admin and pm)
  const canManageStatus = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;
  const canManageTicketAttributes = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;

  // Filter states from Redux
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm);
  const searchInputRef = React.useRef(null);
  const [selectedProject, setSelectedProject] = useState(filters.selectedProject);
  const [selectedPriority, setSelectedPriority] = useState(filters.selectedPriority);
  const [selectedAssignee, setSelectedAssignee] = useState(filters.selectedAssignee);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState(filters.selectedCreatedBy || FILTER_VALUES.ALL);
  const [selectedTaskType, setSelectedTaskType] = useState(filters.selectedTaskType);
  const [selectedStatus, setSelectedStatus] = useState(filters.selectedStatus);
  const [selectedLabels, setSelectedLabels] = useState(filters.selectedLabels || [FILTER_VALUES.ALL]);
  const [selectedSprint, setSelectedSprint] = useState(filters.selectedSprint || [FILTER_VALUES.ALL]);
  const [selectedEpic, setSelectedEpic] = useState(filters.selectedEpic || FILTER_VALUES.ALL);
  const [startDate, setStartDate] = useState(filters.startDate || '');
  const [dueDate, setDueDate] = useState(filters.dueDate || '');
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  const searchTimeoutRef = React.useRef(null);

  const currentUnreadCount = (selectedProject && selectedProject !== FILTER_VALUES.ALL) 
    ? (unreadCountByProject?.[selectedProject] || 0) 
    : unreadCount;

  const getNotificationBadgeLabel = useCallback((count) => {
    if (!count || count < 1) return '';
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.HUNDRED) {
      return t('dashboard.notificationsBadge.ninetyNinePlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.FIFTY) {
      return t('dashboard.notificationsBadge.fiftyPlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.THIRTY) {
      return t('dashboard.notificationsBadge.thirtyPlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.TWENTY) {
      return t('dashboard.notificationsBadge.twentyPlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.TEN) {
      return t('dashboard.notificationsBadge.ninePlus');
    }
    return String(count);
  }, [t]);
  
  const notificationBadgeLabel = useMemo(
    () => getNotificationBadgeLabel(currentUnreadCount),
    [currentUnreadCount, getNotificationBadgeLabel]
  );

  const selectedProjectDetails = projectDropdownsByUser?.find(
    (project) => String(project.id) === String(selectedProject)
  );
  // Sprint filters use different backend fields by project type:
  // Scrum uses sprint_details.scrum_sprint_id, while Kanban keeps tickets.sprint_id.
  const isSelectedProjectScrum = (selectedProjectDetails?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
  const scrumBacklogDefaultProjectRef = React.useRef(null);
  useEffect(() => {
    if (!isSelectedProjectScrum || selectedProject === FILTER_VALUES.ALL || scrumBacklogDefaultProjectRef.current === selectedProject) return;
    scrumBacklogDefaultProjectRef.current = selectedProject;
    // Initial ticket loads in Scrum projects return backlog by default. Keep the
    // visible filter aligned with that result until the user chooses a sprint.
    if (!filters.selectedSprint?.length || filters.selectedSprint.includes(FILTER_VALUES.ALL)) {
      setSelectedSprint([SCRUM_BACKLOG_SENTINEL]);
      dispatch(setFilters({ selectedSprint: [SCRUM_BACKLOG_SENTINEL] }));
    }
  }, [isSelectedProjectScrum, selectedProject, filters.selectedSprint, dispatch]);

  const normalizeDateString = useCallback((value) => {
    if (!value || typeof value !== 'string') return null;
    return getLocalDatePart(value) || null;
  }, []);

  const getCompletedProjectDateRestriction = useCallback(({
    projectId,
    previousStartDate = null,
    previousDueDate = null,
    nextStartDate = null,
    nextDueDate = null
  }) => {
    const project = projectDropdownsByUser?.find(item => String(item.id) === String(projectId));
    const projectDueDate = normalizeDateString(project?.due_date);
    if (!project || project.status !== PROJECT_STATUS.COMPLETED || !projectDueDate) return null;

    const previousStart = normalizeDateString(previousStartDate);
    const previousDue = normalizeDateString(previousDueDate);
    const nextStart = normalizeDateString(nextStartDate);
    const nextDue = normalizeDateString(nextDueDate);

    const startExceeded = nextStart && nextStart > projectDueDate && nextStart !== previousStart;
    const dueExceeded = nextDue && nextDue > projectDueDate && nextDue !== previousDue;

    if (!startExceeded && !dueExceeded) return null;

    return {
      projectId: project.id,
      projectDueDate
    };
  }, [normalizeDateString, projectDropdownsByUser]);

  const closeProjectStatusModal = useCallback(() => {
    setIsProjectStatusModalOpen(false);
    setProjectStatusForModal(null);
    setProjectStatusModalContent(null);
    pendingProjectReactivationActionRef.current = null;
  }, []);

  const openCompletedProjectDateWarning = useCallback(({ projectId, projectDueDate, onConfirm }) => {
    pendingProjectReactivationActionRef.current = { projectId, onConfirm };
    setProjectStatusForModal(PROJECT_STATUS.COMPLETED);
    setProjectStatusModalContent({
      icon: 'CheckCircle',
      colorClass: 'info',
      title: t('projects.completedProjectDateLimitTitle'),
      message: t('projects.completedProjectDateLimitMessage', { date: projectDueDate }),
      actionLabel: t('projects.reactivateAndContinue'),
      loadingLabel: t('projects.reactivatingAndContinuing')
    });
    setIsProjectStatusModalOpen(true);
  }, [t]);

  // Calculate dynamic flex shrink based on 7 char threshold
  const projectNameLength = selectedProjectDetails?.name?.length || 0;
  const spaceNameLength = selectedProjectDetails?.space_name?.length || 0;
  const CHAR_LIMIT = 7;

  let projectFlexShrink = 1;
  let spaceFlexShrink = 1;

  if (selectedProjectDetails?.space_name) {
    const projectLong = projectNameLength > CHAR_LIMIT;
    const spaceLong = spaceNameLength > CHAR_LIMIT;

    if (projectLong && !spaceLong) {
      // Only project is long - it should truncate
      projectFlexShrink = 100;
      spaceFlexShrink = 0;
    } else if (spaceLong && !projectLong) {
      // Only space is long - it should truncate
      projectFlexShrink = 0;
      spaceFlexShrink = 100;
    } else {
      // Both long or both short - shrink equally
      projectFlexShrink = 1;
      spaceFlexShrink = 1;
    }
  } else {
    projectFlexShrink = 0;
  }

  const sprintHeaderText = selectedProjectDetails
    ? `${selectedProjectDetails.name || t('dashboard.project')}${selectedProjectDetails.space_name ? ` - ${selectedProjectDetails.space_name}` : ''}`
    : projectDropdownsByUser?.length === 0
      ? t('dashboard.noProjectSelected')
      : selectedProject === FILTER_VALUES.ALL
        ? t('dashboard.noProjectSelected')
        : t('dashboard.selectProject');
  const selectedProjectStatusConfig = selectedProjectDetails?.status
    ? PROJECT_STATUS_MODAL_CONFIG[selectedProjectDetails.status]
    : null;
  const SelectedProjectStatusIcon = selectedProjectStatusConfig?.icon === 'PauseCircle'
    ? PauseCircle
    : selectedProjectStatusConfig?.icon === 'RemoveCircleOutline'
      ? RemoveCircleOutline
      : CheckCircle;
  const searchTermRef = React.useRef(filters.searchTerm || '');
  const isInitialLoad = React.useRef(true);
  const dropdownsFetched = React.useRef(false);
  const prevSelectedProjectRef = React.useRef(selectedProject);
  const lastFetchKeyRef = React.useRef(null);
  const hasStartedLoadingRef = React.useRef(false);

  if (dropdownsLoading || ticketsLoading) {
    hasStartedLoadingRef.current = true;
  }

  const [initialLoadDone, setInitialLoadDone] = useState(() => {
    const hasTickets = tickets && Object.keys(tickets).length > 0;
    const hasStatuses = dropdowns?.statuses && dropdowns.statuses.length > 0;
    return !!(hasTickets || hasStatuses);
  });

  useEffect(() => {
    if (!dropdownsLoading && !projectDropdownsByUserLoading && !ticketsLoading && dropdownsFetched.current && hasStartedLoadingRef.current) {
      setInitialLoadDone(true);
    }
  }, [dropdownsLoading, projectDropdownsByUserLoading, ticketsLoading]);

  useEffect(() => {
    if (!projectDropdownsByUserLoading && preferenceLoaded && projectDropdownsByUser.length === 0) {
      setInitialLoadDone(true);
    }
  }, [projectDropdownsByUserLoading, preferenceLoaded, projectDropdownsByUser.length]);

  useEffect(() => {
    if (prevSelectedProjectRef.current !== selectedProject) {
      prevSelectedProjectRef.current = selectedProject;
      if (selectedProject !== FILTER_VALUES.ALL) {
        setInitialLoadDone(false);
      }
    }
  }, [selectedProject]);

  const [selectedSort, setSelectedSort] = useState(filters.selectedSort || 'NEWEST_FIRST');

  // Column-wise infinite scrolling states
  const columnScrollRefs = React.useRef({});

  // Callback ref to avoid setting refs on every render
  const setColumnRef = React.useCallback((el, columnId) => {
    if (el) {
      columnScrollRefs.current[columnId] = el;
    }
  }, []);

  // Load initial view mode from localStorage or default to BOARD
  const initialViewMode = (typeof window !== 'undefined' && localStorage.getItem('lastSelectedViewMode')) || VIEW_MODES.BOARD;

  // UI states
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [showCalendarView, setShowCalendarView] = useState(initialViewMode === VIEW_MODES.CALENDAR);
  const [showGanttView, setShowGanttView] = useState(initialViewMode === VIEW_MODES.GANTT);
  const [showBoardView, setShowBoardView] = useState(initialViewMode === VIEW_MODES.BOARD);
  const [showListView, setShowListView] = useState(initialViewMode === VIEW_MODES.LIST);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [isDeleteTaskModalOpen, setIsDeleteTaskModalOpen] = useState(false);
  const [isTaskViewModalOpen, setIsTaskViewModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isAddColumnModalOpen, setIsAddColumnModalOpen] = useState(false);
  const [isEditColumnModalOpen, setIsEditColumnModalOpen] = useState(false);
  const [isDeleteColumnModalOpen, setIsDeleteColumnModalOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [loadingTaskId, setLoadingTaskId] = useState(null);
  const [taskViewInitialEditMode, setTaskViewInitialEditMode] = useState(false);
  const [newlyCreatedTaskId, setNewlyCreatedTaskId] = useState(null);

  useEffect(() => {
    if (!newlyCreatedTaskId) return undefined;
    const timer = setTimeout(() => {
      setNewlyCreatedTaskId(null);
    }, UI.animationMs.newlyCreatedTaskDuration);
    return () => clearTimeout(timer);
  }, [newlyCreatedTaskId]);

  // Multiselect state - per column
  const [selectedTasksByColumn, setSelectedTasksByColumn] = useState({});
  const [selectAllModeByColumn, setSelectAllModeByColumn] = useState({}); // Track which columns have select all active
  const [bulkModeColumnId, setBulkModeColumnId] = useState(null);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [bulkEditColumnId, setBulkEditColumnId] = useState(null);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteColumnId, setBulkDeleteColumnId] = useState(null);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const kebabMenu = useMenuState();

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkingTask, setLinkingTask] = useState(null);
  const [isViewLinksModalOpen, setIsViewLinksModalOpen] = useState(false);
  const [viewingLinksTask, setViewingLinksTask] = useState(null);
  const [isEditLinkModalOpen, setIsEditLinkModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null);
  const previousUnreadCountRef = useRef(0);
  const [bellRinging, setBellRinging] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const [audioPromptReady, setAudioPromptReady] = useState(false);
  const audioEnabledThisSession = useRef(false);
  const activeNotificationProjectIdRef = useRef(null);
  const [isProjectStatusModalOpen, setIsProjectStatusModalOpen] = useState(false);
  const [projectStatusForModal, setProjectStatusForModal] = useState(null);
  const [activatingProject, setActivatingProject] = useState(false);
  const [projectStatusModalContent, setProjectStatusModalContent] = useState(null);
  const pendingProjectReactivationActionRef = useRef(null);
  const notificationSoundManager = useMemo(
    () => createNotificationSoundManager({
      getProjectId: () => activeNotificationProjectIdRef.current
    }),
    []
  );

  useEffect(() => {
    notificationSoundManager.syncDebugState();
  }, [notificationSoundManager]);

  useEffect(() => {
    activeNotificationProjectIdRef.current = selectedProject && selectedProject !== FILTER_VALUES.ALL
      ? selectedProject
      : null;
    notificationSoundManager.syncDebugState();
  }, [notificationSoundManager, selectedProject]);

  // Clear audio session flag on page reload (not on navigation)
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('audioEnabledThisSession');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleSelectViewMode = (mode) => {
    setShowGanttView(mode === VIEW_MODES.GANTT);
    setShowCalendarView(mode === VIEW_MODES.CALENDAR);
    setShowBoardView(mode === VIEW_MODES.BOARD);
    setShowListView(mode === VIEW_MODES.LIST);
    setViewMode(mode);
    localStorage.setItem('lastSelectedViewMode', mode);
  };
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [taskUpdateHandler, setTaskUpdateHandler] = useState(null);


  // Memoize searchTickets function to prevent infinite loops
  const searchTicketsCallback = useCallback(async (searchTerm) => {
    try {
      const result = await dispatch(searchTickets({ projectId: selectedProject, searchTerm })).unwrap();
      return result.map(ticket => ({
        id: ticket.id,
        number: ticket?.ticket_number,
        title: ticket?.title,
        status: ticket?.priority
      }));
    } catch (error) {
      console.error('Search tickets failed:', error);
      return [];
    }
  }, [dispatch, selectedProject]);

  // Memoize searchEpics function for epic selector
  const searchEpicsCallback = useCallback(async ({ projectId, searchTerm, task_type }) => {
    try {
      const result = await dispatch(
        searchTickets({
          projectId,
          searchTerm,
          task_type
        })
      ).unwrap();

      return result.map(ticket => ({
        id: ticket.id,
        ticket_number: ticket?.ticket_number,
        title: ticket?.title,
        start_date: ticket?.start_date,
        due_date: ticket?.due_date
      }));
    } catch (error) {
      return [];
    }
  }, [dispatch]);

  // Memoize relationshipTypes to prevent recreating on every render
  const memoizedRelationshipTypes = useMemo(() =>
    dropdowns.relationship_types?.map(rt => ({ value: rt.id, label: rt.name, colour: rt.colour })) || [],
    [dropdowns.relationship_types]
  );

  // Memoize initialData for edit link modal
  const memoizedEditLinkInitialData = useMemo(() => {
    if (!editingLink) return null;
    return {
      relationshipType: editingLink.relationshipType,
      targetTicket: {
        id: editingLink.ticket.id,
        title: editingLink.ticket.title,
        number: editingLink.ticket.ticket_number
      }
    };
  }, [editingLink]);

  // Memoized boolean map for bulk mode to minimize re-renders
  const isBulkMode = useMemo(() => {
    return bulkModeColumnId !== null;
  }, [bulkModeColumnId]);

  // Memoized arrays for API calls to avoid repeated Array.from() calls
  const selectedTaskArrays = useMemo(() => {
    const arrays = {};
    Object.entries(selectedTasksByColumn).forEach(([columnId, taskSet]) => {
      if (taskSet && taskSet.size > 0) {
        arrays[columnId] = Array.from(taskSet);
      }
    });
    return arrays;
  }, [selectedTasksByColumn]);

  // Memoized selected task sets for O(1) lookups
  const selectedTaskSets = useMemo(() => {
    return selectedTasksByColumn;
  }, [selectedTasksByColumn]);

  // Check if selection limit reached
  const isSelectionLimitReached = useMemo(() => {
    const currentSelected = selectedTasksByColumn[bulkModeColumnId];
    return currentSelected && currentSelected.size >= 100;
  }, [selectedTasksByColumn, bulkModeColumnId]);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isMoveConfirmOpen, setIsMoveConfirmOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [isMoveConfirmLoading, setIsMoveConfirmLoading] = useState(false);
  const boardScrollRef = React.useRef(null);
  const [preselectedStatusId, setPreselectedStatusId] = useState(null);
  const [selectedDateForTask, setSelectedDateForTask] = useState(null);

  // Board visits retain only the project, never an implicitly reapplied combination.
  useEffect(() => () => {
    const project = store.getState().tickets.filters.selectedProject;
    dispatch(setFilters({ ...DEFAULT_FILTERS, selectedProject: project, epicStartDate: null, epicDueDate: null }));
  }, [dispatch, store]);
  const [savedFilterRevision, setSavedFilterRevision] = useState(0);
  const savedFilterCriteria = {
    searchTerm, selectedProject, selectedPriority, selectedAssignee, selectedCreatedBy,
    selectedTaskType, selectedStatus, selectedLabels, selectedSprint, selectedEpic,
    startDate, dueDate, selectedSort
  };
  const applySavedFilter = async (criteria) => {
    const project = projectDropdownsByUser.find(p => String(p.id) === String(criteria.selectedProject));
    if (!project) throw new Error(t('savedFilters.projectUnavailable'));
    const options = await dispatch(fetchTicketDropdowns(project.id)).unwrap();
    const unavailable = new Set();
    const savedFilterFieldLabels = { "selectedPriority": "dashboard.priority", "selectedStatus": "dashboard.status", "selectedAssignee": "dashboard.assignee", "selectedCreatedBy": "dashboard.createdBy", "selectedTaskType": "dashboard.taskType", "selectedLabels": "dashboard.labels", "selectedSprint": "taskModal.sprint" };
    const unavailableFields = getUnavailableSavedFilterFields(criteria, options, String(project.project_type).toUpperCase() === 'SCRUM');
    unavailableFields.forEach(field => unavailable.add(t(savedFilterFieldLabels[field])));
    if (criteria.selectedEpic && criteria.selectedEpic !== FILTER_VALUES.ALL) {
      try { await api.get(ENDPOINTS.TICKETS_DETAILS(criteria.selectedEpic)); }
      catch { unavailable.add(t('dashboard.epic')); }
    }
    lastFetchKeyRef.current = null;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTermRef.current = criteria.searchTerm || '';
    setSearchTerm(criteria.searchTerm || '');
    if (searchInputRef.current) searchInputRef.current.value = criteria.searchTerm || '';
    setSelectedProject(project.id);
    setSelectedPriority(criteria.selectedPriority);
    setSelectedAssignee(criteria.selectedAssignee);
    setSelectedCreatedBy(criteria.selectedCreatedBy);
    setSelectedTaskType(criteria.selectedTaskType);
    setSelectedStatus(criteria.selectedStatus);
    setSelectedLabels(criteria.selectedLabels);
    setSelectedSprint(criteria.selectedSprint);
    setSelectedEpic(criteria.selectedEpic);
    setStartDate(criteria.startDate);
    setDueDate(criteria.dueDate);
    setSelectedSort(criteria.selectedSort);
    dispatch(setFilters({ ...criteria, epicStartDate: null, epicDueDate: null }));
    setSavedFilterRevision(value => value + 1);
    dispatch(saveUserPreference({ preferenceKey: 'last_selected_project', preferenceValue: { project_id: project.id } }));
    if (unavailable.size) toast.info(t('savedFilters.criteriaUnavailable', { criteria: [...unavailable].join(', ') }));
  };
  const savedFilters = useSavedFilters({ criteria: savedFilterCriteria, onApply: applySavedFilter, enabled: selectedProject !== FILTER_VALUES.ALL });

  // Helper function to get current filters with optional overrides
  const getCurrentFilters = React.useCallback((overrides = {}) => {
    const effectiveSprint = overrides.sprint !== undefined ? overrides.sprint : selectedSprint;
    const hasSprintFilter = effectiveSprint.length > 0 && !effectiveSprint.includes(FILTER_VALUES.ALL);
    const filters = {
      search: overrides.search !== undefined ? overrides.search : (searchTermRef.current || undefined),
      priority: overrides.priority !== undefined ? (overrides.priority !== FILTER_VALUES.ALL ? overrides.priority : undefined) : (selectedPriority !== FILTER_VALUES.ALL ? selectedPriority : undefined),
      status: overrides.status !== undefined ? (overrides.status !== FILTER_VALUES.ALL ? overrides.status : undefined) : (selectedStatus !== FILTER_VALUES.ALL ? selectedStatus : undefined),
      assignee: overrides.assignee !== undefined ? (overrides.assignee !== FILTER_VALUES.ALL ? overrides.assignee : undefined) : (selectedAssignee !== FILTER_VALUES.ALL ? selectedAssignee : undefined),
      created_by: overrides.created_by !== undefined ? (overrides.created_by !== FILTER_VALUES.ALL ? overrides.created_by : undefined) : (selectedCreatedBy !== FILTER_VALUES.ALL ? selectedCreatedBy : undefined),
      taskType: overrides.taskType !== undefined ? (overrides.taskType !== FILTER_VALUES.ALL ? overrides.taskType : undefined) : (selectedTaskType !== FILTER_VALUES.ALL ? selectedTaskType : undefined),
      labels: overrides.labels !== undefined ? (overrides.labels.length > 0 && !overrides.labels.includes(FILTER_VALUES.ALL) ? overrides.labels : undefined) : (selectedLabels.length > 0 && !selectedLabels.includes(FILTER_VALUES.ALL) ? selectedLabels : undefined),
      // Preserve existing Kanban sprint behavior, but send Scrum sprint selection
      // through the dedicated API key expected by Scrum ticket queries.
      sprint: !isSelectedProjectScrum && hasSprintFilter && !normalizeSprintIds(effectiveSprint, FILTER_VALUES.ALL).includes(SCRUM_BACKLOG_SENTINEL) ? effectiveSprint : undefined,
      scrum_sprint_id: (() => {
        const sprintIds = normalizeSprintIds(effectiveSprint, FILTER_VALUES.ALL);
        if ((!isSelectedProjectScrum && !sprintIds.includes(SCRUM_BACKLOG_SENTINEL)) || !hasSprintFilter) return undefined;
        return sprintIds.length ? sprintIds.join(',') : undefined;
      })(),
      epic: overrides.epic !== undefined ? (overrides.epic !== FILTER_VALUES.ALL ? overrides.epic : undefined) : (selectedEpic !== FILTER_VALUES.ALL ? selectedEpic : undefined),
      start_date: overrides.start_date !== undefined ? overrides.start_date : (startDate || undefined),
      due_date: overrides.due_date !== undefined ? overrides.due_date : (dueDate || undefined),
      sort: (viewMode === VIEW_MODES.GANTT || viewMode === VIEW_MODES.CALENDAR)
        ? undefined
        : (overrides.sort !== undefined ? overrides.sort : (selectedSort || undefined))
    };
    Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);
    return filters;
  }, [isSelectedProjectScrum, selectedPriority, selectedStatus, selectedAssignee, selectedCreatedBy, selectedTaskType, selectedLabels, selectedSprint, selectedEpic, startDate, dueDate, selectedSort, viewMode]);

  useEffect(() => {
    if (viewMode !== VIEW_MODES.GANTT) return;
    if (!selectedEpic || selectedEpic === FILTER_VALUES.ALL) return;
    if (!ticketDetails?.id) return;
    if (String(ticketDetails.id) !== String(selectedEpic)) return;

    const nextEpicStart = ticketDetails.start_date || null;
    const nextEpicDue = ticketDetails.end_date || ticketDetails.due_date || null;

    if (filters.epicStartDate !== nextEpicStart || filters.epicDueDate !== nextEpicDue) {
      dispatch(setFilters({
        epicStartDate: nextEpicStart,
        epicDueDate: nextEpicDue
      }));
    }
  }, [viewMode, selectedEpic, ticketDetails?.id, ticketDetails?.start_date, ticketDetails?.end_date, ticketDetails?.due_date, filters.epicStartDate, filters.epicDueDate, dispatch]);
  // Task handlers
  const handleEdit = React.useCallback(async (task, options = {}) => {
    if (options?.skipFetch) {
      setSelectedTask(task);
      setIsEditTaskModalOpen(true);
      return;
    }
    const preFetchDetails = store.getState().tickets.ticketDetails;
    try {
      const detailedTask = await fetchAndFormatTicketForEdit(dispatch, task.id);
      // A detail fetch may resolve with the pre-update server snapshot while an
      // optimistic date request is still pending. Prefer the current Redux detail
      // values for these fields so Edit opens with the optimistic selection.
      const currentDetails = preFetchDetails && String(preFetchDetails.id) === String(task.id)
        ? preFetchDetails
        : null;
      if (currentDetails) {
        const optimisticDateFields = {};
        ['start_date', 'end_date', 'due_date', 'has_due_time'].forEach((field) => {
          if (Object.prototype.hasOwnProperty.call(currentDetails, field)) {
            optimisticDateFields[field] = currentDetails[field];
          }
        });
        if (Object.keys(optimisticDateFields).length > 0) {
          // fetchTicketDetails.fulfilled replaces ticketDetails. Restore the
          // pre-fetch optimistic dates so inline Edit reads the current values.
          dispatch(optimisticMergeTicket({ ticketId: task.id, updates: optimisticDateFields }));
        }
      }
      setSelectedTask(currentDetails ? {
        ...detailedTask,
        startDate: Object.prototype.hasOwnProperty.call(currentDetails, 'start_date')
          ? (getLocalDatePart(currentDetails.start_date) || '')
          : detailedTask.startDate,
        dueDate: Object.prototype.hasOwnProperty.call(currentDetails, 'end_date')
          ? (getLocalDatePart(currentDetails.end_date) || '')
          : Object.prototype.hasOwnProperty.call(currentDetails, 'due_date')
            ? (getLocalDatePart(currentDetails.due_date) || '')
            : detailedTask.dueDate,
        dueTime: Object.prototype.hasOwnProperty.call(currentDetails, 'has_due_time')
          ? (currentDetails.has_due_time
            ? getLocalTimePart(currentDetails.end_date || currentDetails.due_date, { includeMidnight: true })
            : '')
          : detailedTask.dueTime,
        has_due_time: Object.prototype.hasOwnProperty.call(currentDetails, 'has_due_time')
          ? currentDetails.has_due_time
          : detailedTask.has_due_time
      } : detailedTask);
    } catch (error) {
      console.error('Failed to fetch task details for editing:', error);
      // Fallback to original task data
      setSelectedTask(task);
    } finally {
      // Ensure the modal opens even if fetching fails
      setTaskViewInitialEditMode(true);
      setIsTaskViewModalOpen(true);
    }
  }, [dispatch, store]);

  const handleDelete = React.useCallback((task) => {
    setSelectedTask(task);
    setIsDeleteTaskModalOpen(true);
  }, []);

  const handleAddTask = async (taskData) => {
    try {
      let calendarTicketPayload = null;

      if (viewMode === VIEW_MODES.CALENDAR) {
        const resolvedTaskType =
          dropdowns.types.find(type => String(type.id) === String(taskData.taskType)) ||
          null;

        const resolvedPriority =
          dropdowns.priorities.find(priority => String(priority.id) === String(taskData.priority)) ||
          null;

        const resolvedStatus =
          dropdowns.statuses.find(status => String(status.id) === String(taskData.status)) ||
          null;

        const resolvedAssignees =
          (taskData.assignees || [])
            .map(assigneeId => dropdowns.assignees.find(assigneeOption => String(assigneeOption.id) === String(assigneeId)))
            .filter(Boolean);

        const normalizedStartDate =
          taskData.startDate ||
          selectedDateForTask ||
          null;

        const normalizedDueDate =
          taskData.dueDate ||
          null;

        calendarTicketPayload = {
          date: normalizedStartDate,
          ticket: {
            id: '',
            title: taskData.title,
            ticket_number: '',
            start_date: normalizedStartDate,
            due_date: normalizedDueDate,
            has_due_time: Boolean(taskData.has_due_time),
            priority: resolvedPriority,
            status: resolvedStatus,
            task_type: resolvedTaskType,
            assignee: resolvedAssignees[0] || null,
            assignees: resolvedAssignees,
            marked_as_completed: false,
            project: taskData.project
          }
        };
      }

      // Close modal first
      setIsAddTaskModalOpen(false);

      const createdTask = await dispatch(createTicket(taskData)).unwrap();
      if (createdTask?.id) {
        setNewlyCreatedTaskId(createdTask.id);
      }

      if (viewMode === VIEW_MODES.CALENDAR) {
        if (calendarTicketPayload && createdTask?.id) {
          dispatch(addCalendarTicket({
            ...calendarTicketPayload,
            ticket: {
              ...calendarTicketPayload.ticket,
              id: createdTask.id,
              title: createdTask?.title || calendarTicketPayload.ticket.title,
              ticket_number: createdTask?.ticket_number || '',
              start_date: createdTask?.start_date || calendarTicketPayload.ticket.start_date,
              due_date: createdTask?.due_date || calendarTicketPayload.ticket.due_date,
              has_due_time: createdTask?.has_due_time ?? calendarTicketPayload.ticket.has_due_time,
              priority: createdTask?.priority || calendarTicketPayload.ticket.priority,
              status: createdTask?.status || calendarTicketPayload.ticket.status,
              task_type: createdTask?.task_type || createdTask?.type || calendarTicketPayload.ticket.task_type,
              assignee: createdTask?.assignee || calendarTicketPayload.ticket.assignee,
              assignees: createdTask?.assignees || calendarTicketPayload.ticket.assignees,
              marked_as_completed: createdTask?.marked_as_completed ?? createdTask?.completed ?? createdTask?.is_completed ?? false,
              project: createdTask?.project || calendarTicketPayload.ticket.project
            }
          }));
        }
        return;
      }

      // Refresh dropdowns to get updated data
      if (taskData.project) {
        await dispatch(fetchTicketDropdowns(taskData.project));
      } else if (selectedProject !== FILTER_VALUES.ALL) {
        await dispatch(fetchTicketDropdowns(selectedProject));
      }

      // Always fetch tickets to refresh all views
      if (taskData.project) {
        await dispatch(fetchTicketsByProject({ projectId: taskData.project, filters: getCurrentFilters() }));
        setSelectedProject(taskData.project);
      } else {
        if (selectedProject !== FILTER_VALUES.ALL) {
          await dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
        }
      }
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  // Column handlers
  const handleAddColumn = async (columnData) => {
    try {
      await dispatch(createTicketStatus({ name: columnData.name, projectId: columnData.projectId, colour: columnData.colour })).unwrap();
      if (selectedProject !== FILTER_VALUES.ALL) {
        await dispatch(fetchTicketDropdowns(selectedProject));
      }
    } catch (error) {
      console.error('Failed to create column:', error);
    }
  };

  const handleEditColumn = React.useCallback((column) => {
    setSelectedColumn(column);
    setIsEditColumnModalOpen(true);
  }, []);

  const handleDeleteColumn = React.useCallback((column) => {
    setSelectedColumn(column);
    setIsDeleteColumnModalOpen(true);
  }, []);

  const handleUpdateColumn = async (columnData) => {
    try {
      await dispatch(updateTicketDropdown({ id: columnData.id, name: columnData.name, colour: columnData.colour, category: 'status' })).unwrap();
      // Kick off background refreshes without awaiting so the modal closes immediately
      if (selectedProject !== FILTER_VALUES.ALL) {
        dispatch(fetchTicketDropdowns(selectedProject));
        dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
      }
    } catch (error) {
      console.error('Failed to update column:', error);
      throw error; // Re-throw so the modal knows it failed
    }
  };

  const handleConfirmDeleteColumn = async (columnData) => {
    try {
      await dispatch(deleteTicketDropdown({ id: columnData.id, category: 'status' })).unwrap();
      // Kick off background refresh without awaiting so the modal closes immediately
      if (selectedProject !== FILTER_VALUES.ALL) {
        dispatch(fetchTicketDropdowns(selectedProject));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
      throw error; // Re-throw so the modal knows it failed
    }
  };

  const performTaskUpdate = useCallback(async (taskData) => {
    try {
      if (!taskData.id) {
        console.error('Task ID is missing');
        return;
      }
      const updatedTicket = await dispatch(updateTicket({ ticketId: taskData.id, ticketData: taskData })).unwrap();

      // Close modal and clear selected task first
      setIsEditTaskModalOpen(false);
      setSelectedTask(null);

      const projectId = typeof taskData.project === 'object' ? taskData.project?.id : taskData.project;

      if (viewMode === VIEW_MODES.GANTT || taskUpdateHandler) {
        if (updatedTicket) {
          dispatch(updateGanttTicket({ ticketId: taskData.id, updates: updatedTicket }));
          dispatch(markSubtaskUpdated());
        }
        return;
      }

      // Refresh dropdowns to get updated data with correct project_id
      if (projectId) {
        await dispatch(fetchTicketDropdowns(projectId));
      }

      // Force refresh with task's project and maintain current filters
      const filters = getCurrentFilters();
      if (projectId) {
        await dispatch(fetchTicketsByProject({ projectId, filters }));
        setSelectedProject(projectId);
      } else {
        if (selectedProject !== FILTER_VALUES.ALL) {
          await dispatch(fetchTicketsByProject({ projectId: selectedProject, filters }));
        }
      }
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  }, [dispatch, viewMode, taskUpdateHandler, getCurrentFilters, selectedProject]);

  const handleUpdateTask = async (taskData) => {
    const projectId = typeof taskData.project === 'object' ? taskData.project?.id : taskData.project;
    const restriction = getCompletedProjectDateRestriction({
      projectId,
      previousStartDate: selectedTask?.startDate || selectedTask?.start_date,
      previousDueDate: selectedTask?.dueDate || selectedTask?.due_date || selectedTask?.end_date,
      nextStartDate: taskData.startDate || taskData.start_date,
      nextDueDate: taskData.dueDate || taskData.due_date
    });

    if (restriction) {
      openCompletedProjectDateWarning({
        projectId: restriction.projectId,
        projectDueDate: restriction.projectDueDate,
        onConfirm: async () => {
          await performTaskUpdate(taskData);
        }
      });
      return;
    }

    await performTaskUpdate(taskData);
  };

  const handleDeleteTask = async (taskData) => {
    try {
      if (!taskData.id) {
        console.error('Task ID is missing');
        return;
      }
      await dispatch(deleteTicket(taskData.id)).unwrap();
      setIsDeleteTaskModalOpen(false);
      // Refresh dropdowns to get updated data
      if (selectedProject !== FILTER_VALUES.ALL) {
        await dispatch(fetchTicketDropdowns(selectedProject));
      }

      // Refresh current project tickets with filters
      if (selectedProject !== FILTER_VALUES.ALL) {
        await dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      const errorMessage = error.message || t('common.deleteError', 'Failed to delete task');
      toast.error(errorMessage);
    }
  };

  const handleCreateFilterPriority = async ({ name, colour }) => {
    if (!name?.trim() || selectedProject === FILTER_VALUES.ALL) return;
    try {
      const created = await dispatch(createTicketPriority({ name: name.trim(), colour, projectId: selectedProject })).unwrap();
      if (created?.id) {
        setSelectedPriority(created.id);
        dispatch(setFilters({ selectedPriority: created.id }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.PRIORITY_CREATE_FAILED;
      toast.error(errorMessage);
    }
  };

  const handleCreateFilterTaskType = async ({ name }) => {
    if (!name?.trim() || selectedProject === FILTER_VALUES.ALL) return;
    try {
      const created = await dispatch(createTicketType({ name: name.trim(), projectId: selectedProject })).unwrap();
      if (created?.id) {
        setSelectedTaskType(created.id);
        dispatch(setFilters({ selectedTaskType: created.id }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TASK_TYPE_CREATE_FAILED;
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleCreateFilterLabel = async ({ name, colour }) => {
    if (!name?.trim() || selectedProject === FILTER_VALUES.ALL) return;
    try {
      const created = await dispatch(createTicketLabel({
        name: name.trim(),
        colour,
        projectId: selectedProject
      })).unwrap();
      if (created?.id) {
        const nextLabels = [...selectedLabels.filter(v => v !== FILTER_VALUES.ALL), created.id];
        setSelectedLabels(nextLabels);
        dispatch(setFilters({ selectedLabels: nextLabels }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.LABEL_CREATE_FAILED;
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleCreateFilterSprint = async ({ name, colour }) => {
    if (!name?.trim() || selectedProject === FILTER_VALUES.ALL) return;
    try {
      const created = await dispatch(createTicketSprint({
        name: name.trim(),
        colour,
        projectId: selectedProject
      })).unwrap();
      if (created?.id) {
        const nextSprints = [...selectedSprint.filter(v => v !== FILTER_VALUES.ALL), created.id];
        setSelectedSprint(nextSprints);
        dispatch(setFilters({ selectedSprint: nextSprints }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.SPRINT_CREATE_FAILED;
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleUpdateFilterPriority = async ({ id, name, colour }) => {
    if (!name?.trim()) return;
    try {
      await dispatch(updateTicketDropdown({
        id,
        name: name.trim(),
        colour,
        category: 'priority'
      })).unwrap();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.PRIORITY_UPDATE_FAILED;
      toast.error(errorMessage);
    }
  };

  const handleUpdateFilterTaskType = async ({ id, name }) => {
    if (!name?.trim()) return;
    try {
      await dispatch(updateTicketDropdown({
        id,
        name: name.trim(),
        category: 'type'
      })).unwrap();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TASK_TYPE_UPDATE_FAILED;
      toast.error(errorMessage);
    }
  };

  const handleUpdateFilterLabel = async ({ id, name, colour }) => {
    if (!name?.trim()) return;
    try {
      await dispatch(updateTicketDropdown({
        id,
        name: name.trim(),
        colour,
        category: 'labels'
      })).unwrap();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.LABEL_UPDATE_FAILED;
      toast.error(errorMessage);
    }
  };

  const handleUpdateFilterSprint = async ({ id, name, colour }) => {
    if (!name?.trim()) return;
    try {
      await dispatch(updateTicketDropdown({
        id,
        name: name.trim(),
        colour,
        category: 'sprint'
      })).unwrap();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.SPRINT_UPDATE_FAILED;
      toast.error(errorMessage);
    }
  };

  const handleDeleteFilterPriority = async (priority) => {
    if (deleteLocksRef.current.priority) return;
    deleteLocksRef.current.priority = true;
    try {
      await dispatch(deleteTicketDropdown({ id: priority.id, category: 'priority' })).unwrap();
      if (selectedPriority === priority.id) {
        setSelectedPriority(FILTER_VALUES.ALL);
        dispatch(setFilters({ selectedPriority: FILTER_VALUES.ALL }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.priority = false;
    }
  };

  const handleDeleteFilterTaskType = async (taskType) => {
    if (deleteLocksRef.current.taskType) return;
    deleteLocksRef.current.taskType = true;
    try {
      await dispatch(deleteTicketDropdown({ id: taskType.id, category: 'type' })).unwrap();
      if (selectedTaskType === taskType.id) {
        setSelectedTaskType(FILTER_VALUES.ALL);
        dispatch(setFilters({ selectedTaskType: FILTER_VALUES.ALL }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.taskType = false;
    }
  };

  const handleDeleteFilterLabel = async (label) => {
    if (deleteLocksRef.current.label) return;
    deleteLocksRef.current.label = true;
    try {
      await dispatch(deleteTicketDropdown({ id: label.id, category: 'labels' })).unwrap();
      if (selectedLabels.includes(label.id)) {
        const nextLabels = selectedLabels.filter(v => v !== FILTER_VALUES.ALL && v !== label.id);
        const normalized = nextLabels.length > 0 ? nextLabels : [FILTER_VALUES.ALL];
        setSelectedLabels(normalized);
        dispatch(setFilters({ selectedLabels: normalized }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.label = false;
    }
  };

  const handleDeleteFilterSprint = async (sprint) => {
    if (deleteLocksRef.current.sprint) return;
    deleteLocksRef.current.sprint = true;
    try {
      await dispatch(deleteTicketDropdown({ id: sprint.id, category: 'sprint' })).unwrap();
      if (selectedSprint.includes(sprint.id)) {
        const nextSprints = selectedSprint.filter(v => v !== FILTER_VALUES.ALL && v !== sprint.id);
        const normalized = nextSprints.length > 0 ? nextSprints : [FILTER_VALUES.ALL];
        setSelectedSprint(normalized);
        dispatch(setFilters({ selectedSprint: normalized }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.sprint = false;
    }
  };



  const handleViewTask = React.useCallback(async (task, updateHandler = null) => {
    // Set loading state to prevent card blinking
    setLoadingTaskId(task.id);

    // Clear previous data first
    dispatch(clearTicketDetails());
    setSelectedTask(task);
    setTaskViewInitialEditMode(false);

    if (task?.id) {
      // Only fetch details and comments initially - other APIs called on tab open
      try {
        await Promise.all([
          dispatch(fetchTicketDetails(task.id)),
          dispatch(fetchTicketComments(task.id)),
          dispatch(fetchWorklogSummary(task.id)),
          dispatch(fetchTicketHistory(task.id))
        ]);
      } catch (error) {
        console.error('Failed to fetch task data:', error);
      }
    }

    // Clear loading state and open modal
    setLoadingTaskId(null);
    setIsTaskViewModalOpen(true);

    // Store the update handler for use in the modal
    if (updateHandler) {
      setTaskUpdateHandler(() => updateHandler);
    }
  }, [dispatch]);



  // Component mounted ref to prevent dispatching after unmount
  const isMountedRef = React.useRef(true);
  const projectDropdownsFetched = React.useRef(false);
  const deleteLocksRef = React.useRef({
    priority: false,
    taskType: false,
    label: false,
    sprint: false
  });

  // Effects
  // Fetch project dropdowns on component mount and when returning to dashboard
  useEffect(() => {
    // Reset the fetched flag when component mounts (returning to dashboard)
    projectDropdownsFetched.current = false;

    // Always fetch project dropdowns when component mounts
    dispatch(fetchProjectDropdownsByUser());
    // Fetch last selected project preference
    dispatch(fetchLastSelectedProject()).finally(() => {
      setPreferenceLoaded(true);
    });


    // Enable audio on any user interaction
    const enableAudioOnInteraction = async () => {
      if (audioEnabledThisSession.current) return;

      const success = await enableAudio();
      if (success) {
        audioEnabledThisSession.current = true;
        sessionStorage.setItem('audioEnabledThisSession', 'true');
        setShowAudioPrompt(false);
      }
      notificationSoundManager.syncDebugState();
    };

    // Check if audio was already enabled in this session and verify it still unlocks.
    const initializeAudioSession = async () => {
      const wasAudioEnabled = sessionStorage.getItem('audioEnabledThisSession') === 'true';
      if (!wasAudioEnabled) {
        setShowAudioPrompt(true);
        setAudioPromptReady(true);
        notificationSoundManager.syncDebugState();
        return;
      }

      const success = await enableAudio();
      if (success) {
        audioEnabledThisSession.current = true;
        setShowAudioPrompt(false);
      } else {
        audioEnabledThisSession.current = false;
        sessionStorage.removeItem('audioEnabledThisSession');
        setShowAudioPrompt(true);
      }
      setAudioPromptReady(true);
      notificationSoundManager.syncDebugState();
    };

    initializeAudioSession();

    // Always attach listeners to enable audio on first interaction
    document.addEventListener('click', enableAudioOnInteraction, { once: true });
    document.addEventListener('keydown', enableAudioOnInteraction, { once: true });
    document.addEventListener('touchstart', enableAudioOnInteraction, { once: true });
    document.addEventListener('mousedown', enableAudioOnInteraction, { once: true });

    return () => {
      isMountedRef.current = false;
      document.removeEventListener('click', enableAudioOnInteraction);
      document.removeEventListener('keydown', enableAudioOnInteraction);
      document.removeEventListener('touchstart', enableAudioOnInteraction);
      document.removeEventListener('mousedown', enableAudioOnInteraction);
    };
  }, [dispatch, notificationSoundManager, user?.id, user?.user_id]);

  // Fetch unread count for current project
  useEffect(() => {
    const userId = user?.id || user?.user_id;
    const hasSelectedProject = selectedProject && String(selectedProject).toUpperCase() !== FILTER_VALUES.ALL;
    if (userId && hasSelectedProject) {
      dispatch(fetchUnreadCount({ userId, projectId: selectedProject }));
    }
  }, [dispatch, selectedProject, user?.id, user?.user_id]);

  // Keep a ref to the current selected project for the bell ringing check
  const selectedProjectRef = useRef(selectedProject);
  useEffect(() => {
    selectedProjectRef.current = selectedProject;
  }, [selectedProject]);

  useEffect(() => {
    const handleNotificationEvent = (event) => {
      const { newNotificationCount, unreadCount, notificationData } = event?.detail || {};
      if (typeof unreadCount === 'number') {
        previousUnreadCountRef.current = unreadCount;
      }
      if (newNotificationCount > 0) {
        // Only ring bell if notification belongs to selected project (or no project filter)
        const currentProject = selectedProjectRef.current;
        const notifProjectId = notificationData?.projectId;
        const isAllProjects = !currentProject || currentProject === FILTER_VALUES.ALL;
        const isMatchingProject = isAllProjects || notifProjectId === currentProject;
        if (isMatchingProject) {
          setBellRinging(true);
          setTimeout(() => setBellRinging(false), 2000);
        }
      }
    };

    window.addEventListener('pmpi:new-notification', handleNotificationEvent);
    return () => {
      window.removeEventListener('pmpi:new-notification', handleNotificationEvent);
    };
  }, []);

  // Mark as fetched when we get a response (success or empty array)
  useEffect(() => {
    if (!projectDropdownsByUserLoading && !projectDropdownsFetched.current) {
      projectDropdownsFetched.current = true;
    }
  }, [projectDropdownsByUserLoading]);

  // Retry fetching dropdowns only if there was an error (not when empty array)
  useEffect(() => {
    // Only retry if:
    // 1. We haven't successfully fetched yet
    // 2. Not currently loading
    // 3. Preference is loaded
    // 4. Array is empty (but this could be a valid empty response)
    if (!projectDropdownsFetched.current && !projectDropdownsByUserLoading && preferenceLoaded && projectDropdownsByUser.length === 0) {
      dispatch(fetchProjectDropdownsByUser());
    }
  }, [projectDropdownsByUser.length, projectDropdownsByUserLoading, preferenceLoaded, dispatch]);

  // Cleanup search timeout and bulk mode on unmount
  useEffect(() => {
    const timeoutRef = searchTimeoutRef.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
      // Exit bulk mode when component unmounts
      if (isBulkMode) {
        setBulkModeColumnId(null);
        setSelectedTasksByColumn({});
      }
    };
  }, [isBulkMode]);

  // Define exitBulkMode before useEffect hooks that use it
  const exitBulkMode = React.useCallback(() => {
    setBulkModeColumnId(null);
    setSelectedTasksByColumn({});
    setSelectAllModeByColumn({});
  }, []);

  // Close kebab menu when bulk mode is exited
  useEffect(() => {
    if (!isBulkMode) {
      kebabMenu.handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkMode]);

  // Exit bulk mode when project changes
  useEffect(() => {
    if (isBulkMode) {
      exitBulkMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, exitBulkMode]);

  // Exit bulk mode when view mode changes
  useEffect(() => {
    if (isBulkMode) {
      exitBulkMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, exitBulkMode]);

  // Auto-select project based on user preference or first available project
  useEffect(() => {
    if (!projectDropdownsByUserLoading && projectDropdownsByUser.length > 0 && preferenceLoaded) {
      const projectExistsInDropdown = (projectId) => (
        projectDropdownsByUser.some(project => String(project.id) === String(projectId))
      );

      // Check if there's a pre-selected project from Redux filters (e.g., from project management navigation)
      const preSelectedProject = filters.selectedProject;

      if (preSelectedProject && preSelectedProject !== FILTER_VALUES.ALL && preSelectedProject !== selectedProject) {
        // Use pre-selected project from Redux
        if (projectExistsInDropdown(preSelectedProject)) {
          const projectDetails = projectDropdownsByUser.find(project => String(project.id) === String(preSelectedProject));
          const isScrumProject = (projectDetails?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
          const useBacklogDefault = isScrumProject && (!filters.selectedSprint?.length || filters.selectedSprint.includes(FILTER_VALUES.ALL));
          const initialSprintSelection = useBacklogDefault ? [SCRUM_BACKLOG_SENTINEL] : filters.selectedSprint;
          const initialFilters = getCurrentFilters();
          delete initialFilters.sprint;
          delete initialFilters.scrum_sprint_id;
          if (isScrumProject) {
            const sprintIds = normalizeSprintIds(initialSprintSelection, FILTER_VALUES.ALL);
            if (sprintIds.length) initialFilters.scrum_sprint_id = sprintIds.join(',');
          }
          setSelectedProject(preSelectedProject);
          if (useBacklogDefault) {
            setSelectedSprint(initialSprintSelection);
            dispatch(setFilters({ selectedSprint: initialSprintSelection }));
          }
          dispatch(fetchTicketDropdowns(preSelectedProject));
          const fetchKey = JSON.stringify({ projectId: preSelectedProject, filters: initialFilters, page: 1, append: false });
          lastFetchKeyRef.current = fetchKey;
          dispatch(fetchTicketsByProject({ projectId: preSelectedProject, filters: initialFilters }));
          dropdownsFetched.current = true;
          return;
        }
      }

      const selectedProjectExists = selectedProject && selectedProject !== FILTER_VALUES.ALL
        ? projectExistsInDropdown(selectedProject)
        : false;

      if (selectedProject === FILTER_VALUES.ALL || !selectedProjectExists) {
        let projectToSelect = null;

        // Check if user has a last selected project preference and if it exists in available projects
        if (lastSelectedProjectId) {
          const preferredProject = projectDropdownsByUser.find(
            p => String(p.id) === String(lastSelectedProjectId)
          );
          if (preferredProject) {
            projectToSelect = preferredProject;
          }
        }

        // Fallback to first Kanban project if no preference or preference not found
        if (!projectToSelect) {
          const kanbanProjects = projectDropdownsByUser.filter(p => (p.project_type || '').toUpperCase() === PROJECT_TYPES.KANBAN.toUpperCase());
          if (kanbanProjects.length > 0) {
            projectToSelect = kanbanProjects[0];
          } else if (projectDropdownsByUser.length > 0) {
            // Extreme fallback if no Kanban projects exist
            projectToSelect = projectDropdownsByUser[0];
          }
        }

        if (projectToSelect) {
          const isScrumProject = (projectToSelect.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
          const initialSprintSelection = isScrumProject ? [SCRUM_BACKLOG_SENTINEL] : [FILTER_VALUES.ALL];
          const initialFilters = getCurrentFilters();
          delete initialFilters.sprint;
          delete initialFilters.scrum_sprint_id;
          if (isScrumProject) initialFilters.scrum_sprint_id = SCRUM_BACKLOG_SENTINEL;
          setSelectedProject(projectToSelect.id);
          if (isScrumProject) setSelectedSprint(initialSprintSelection);
          dispatch(setFilters({ selectedProject: projectToSelect.id, ...(isScrumProject ? { selectedSprint: initialSprintSelection } : {}) }));
          dispatch(fetchTicketDropdowns(projectToSelect.id));
          const fetchKey = JSON.stringify({ projectId: projectToSelect.id, filters: initialFilters, page: 1, append: false });
          lastFetchKeyRef.current = fetchKey;
          dispatch(fetchTicketsByProject({ projectId: projectToSelect.id, filters: initialFilters }));

          // Save selected project as user preference
          dispatch(saveUserPreference({
            preferenceKey: 'last_selected_project',
            preferenceValue: { project_id: projectToSelect.id }
          }));
          dropdownsFetched.current = true;
        }
      } else if (selectedProject !== FILTER_VALUES.ALL && !dropdownsFetched.current) {
        // Only fetch if dropdowns haven't been fetched yet
        const projectDetails = projectDropdownsByUser.find(project => String(project.id) === String(selectedProject));
        const isScrumProject = (projectDetails?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
        const useBacklogDefault = isScrumProject && (!filters.selectedSprint?.length || filters.selectedSprint.includes(FILTER_VALUES.ALL));
        const initialSprintSelection = useBacklogDefault ? [SCRUM_BACKLOG_SENTINEL] : filters.selectedSprint;
        const initialFilters = getCurrentFilters();
        delete initialFilters.sprint;
        delete initialFilters.scrum_sprint_id;
        if (isScrumProject) {
          const sprintIds = normalizeSprintIds(initialSprintSelection, FILTER_VALUES.ALL);
          if (sprintIds.length) initialFilters.scrum_sprint_id = sprintIds.join(',');
        }
        if (useBacklogDefault) {
          setSelectedSprint(initialSprintSelection);
          dispatch(setFilters({ selectedSprint: initialSprintSelection }));
        }
        dispatch(fetchTicketDropdowns(selectedProject));
        const fetchKey = JSON.stringify({ projectId: selectedProject, filters: initialFilters, page: 1, append: false });
        lastFetchKeyRef.current = fetchKey;
        dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: initialFilters }));
        dropdownsFetched.current = true;
      }
    } else if (projectDropdownsByUser.length === 0 && selectedProject !== FILTER_VALUES.ALL) {
      setSelectedProject(FILTER_VALUES.ALL);
      dispatch(setFilters({ selectedProject: FILTER_VALUES.ALL }));
    }
  }, [projectDropdownsByUserLoading, projectDropdownsByUser, selectedProject, filters.selectedProject, lastSelectedProjectId, preferenceLoaded, dispatch, getCurrentFilters]);





  // Track if filters have changed to prevent duplicate API calls
  const filtersChanged = React.useRef(false);
  const lastViewModeRef = React.useRef(viewMode);

  // Fetch tickets when project changes (but not on initial load)
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    if (viewMode === VIEW_MODES.GANTT || viewMode === VIEW_MODES.CALENDAR) {
      filtersChanged.current = false;
      return;
    }
    if (selectedProject !== FILTER_VALUES.ALL && !filtersChanged.current) {
      const fetchKey = JSON.stringify({ projectId: selectedProject, filters: getCurrentFilters(), page: 1, append: false });
      if (lastFetchKeyRef.current !== fetchKey) {
        lastFetchKeyRef.current = fetchKey;
        dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
      }
    }
    filtersChanged.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, dispatch, viewMode]);

  // Sync local filter state when Redux filters change externally (e.g. on label deletion)
  useEffect(() => {
    setSelectedPriority(filters.selectedPriority || FILTER_VALUES.ALL);
    setSelectedAssignee(filters.selectedAssignee || FILTER_VALUES.ALL);
    setSelectedCreatedBy(filters.selectedCreatedBy || FILTER_VALUES.ALL);
    setSelectedTaskType(filters.selectedTaskType || FILTER_VALUES.ALL);
    setSelectedStatus(filters.selectedStatus || FILTER_VALUES.ALL);
    setSelectedLabels(filters.selectedLabels || [FILTER_VALUES.ALL]);
    setSelectedSprint(filters.selectedSprint || [FILTER_VALUES.ALL]);
    setSelectedEpic(filters.selectedEpic || FILTER_VALUES.ALL);
  }, [
    filters.selectedPriority,
    filters.selectedAssignee,
    filters.selectedCreatedBy,
    filters.selectedTaskType,
    filters.selectedStatus,
    filters.selectedLabels,
    filters.selectedSprint,
    filters.selectedEpic
  ]);

  // Fetch tickets when filters change (excluding project and search)
  useEffect(() => {
    // Don't fetch tickets API when in Gantt or Calendar view
    if (viewMode === VIEW_MODES.GANTT || viewMode === VIEW_MODES.CALENDAR) {
      lastViewModeRef.current = viewMode;
      return;
    }

    // Avoid duplicate fetch on viewMode switch (project-change effect already runs)
    if (lastViewModeRef.current !== viewMode) {
      lastViewModeRef.current = viewMode;
      return;
    }

    if (selectedProject !== FILTER_VALUES.ALL && !isInitialLoad.current && dropdownsFetched.current) {
      const fetchKey = JSON.stringify({ projectId: selectedProject, filters: getCurrentFilters(), page: 1, append: false });
      if (lastFetchKeyRef.current !== fetchKey) {
        lastFetchKeyRef.current = fetchKey;
        filtersChanged.current = true;
        dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters(), page: 1, append: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterRevision, selectedPriority, selectedAssignee, selectedCreatedBy, selectedTaskType, selectedStatus, selectedLabels, selectedSprint, selectedEpic, startDate, dueDate, selectedSort, dispatch, selectedProject, viewMode]);

  // Initialize column pagination when tickets are loaded
  useEffect(() => {
    if (dropdowns.statuses.length > 0 && Object.keys(tickets).length > 0) {
      dropdowns.statuses.forEach(status => {
        const statusData = tickets[status.name];
        const currentCount = statusData?.tickets?.length || 0;
        const totalCount = statusData?.total || 0;
        const hasMore = currentCount < totalCount; // Has more if current < total

        if (!columnPagination?.[status.id]) {
          dispatch(initializeColumnPagination({
            statusId: status.id,
            hasMore: hasMore,
            page: 1,
            loading: false
          }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.statuses, tickets, dispatch]);

  // Helper function to format task dates consistently
  const formatTaskDate = React.useCallback((dateString, timeString) => {
    if (!dateString) return t('dashboard.noDueDate');
    const d = parseBackendDate(dateString) || new Date(dateString);
    if (isNaN(d.getTime())) return t('dashboard.noDueDate');
    const day = d.getDate();
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear().toString().slice(-2);
    let dateText = `${day} ${month} ${year}`;
    
    if (timeString) {
      const [h, m] = timeString.split(':');
      if (h !== undefined && m !== undefined) {
        let hour = parseInt(h, 10);
        const ampm = hour >= 12 ? TIME_FORMATS.PM : TIME_FORMATS.AM;
        hour = hour % 12 || 12;
        dateText += `, ${hour}:${m} ${ampm}`;
      }
    } else if (dateString.includes('T')) {
      const isMidnightUTC = dateString.endsWith('T00:00:00.000Z') || dateString.endsWith('T00:00:00Z');
      if (!isMidnightUTC) {
        let hour = d.getHours();
        const min = d.getMinutes();
        const ampm = hour >= 12 ? TIME_FORMATS.PM : TIME_FORMATS.AM;
        hour = hour % 12 || 12;
        const minStr = String(min).padStart(2, '0');
        dateText += `, ${hour}:${minStr} ${ampm}`;
      }
    }
    return dateText;
  }, [t]);

  // Optimized tasks by status mapping for O(1) lookups
  const tasksByStatusMap = useMemo(() => {
    const map = new Map();
    const safeTickets = tickets || {};
    const safeStatuses = dropdowns?.statuses || [];

    Object.entries(safeTickets).forEach(([statusName, statusData]) => {
      const safeStatusData = statusData || { tickets: [] };
      const safeTickets = safeStatusData.tickets || [];
      const status = safeStatuses.find(s => s?.name === statusName);

      if (status?.id) {
        const tasks = safeTickets.map(ticket => {
          if (!ticket?.id) return null;
          // Keep raw due date for exact overdue checks; formatted value stays compact for the card.
          const hasDueTime = hasEffectiveDueTime(ticket.has_due_time, ticket.due_date);

          return {
            id: ticket.id,
            title: ticket.title || '',
            ticket_number: ticket.ticket_number || '',
            parent_ticket_number: ticket.parent_ticket_number || '',
            code: ticket.parent_ticket_number
              ? `${ticket.parent_ticket_number} > ${ticket.ticket_number || 0}`
              : `${ticket.ticket_number || 0}`,
            dueDate: formatTaskDate(ticket.due_date, ticket.due_time),
            dueDateRaw: ticket.due_date,
            hasDueTime,
            dueDateTooltip: formatDateWithOptionalTime(ticket.due_date, hasDueTime, { fallback: t('dashboard.noDueDate') }),
            priority: ticket?.priority,
            status: status.id,
            marked_as_pin: Boolean(ticket.marked_as_pin),
            assignee: {
              name: ticket.assignee || '',
              avatar: ticket.assignee ? ticket.assignee.slice(0, 2).toUpperCase() : 'UN'
            },
            assignees: ticket.assignees || [],
            project: ticket.project || '',
            sub_task_count: ticket.sub_task_count || 0,
            completed: ticket.marked_as_completed || ticket.completed || ticket.is_completed || false,
            labels: ticket.labels || [],
            estimateTime: minutesToTimeString(ticket.estimate_time_in_minutes),
            linked_tickets: ticket.linked_tickets || {}
          };
        }).filter(Boolean);

        map.set(status.id, tasks);
      }
    });

    return map;
  }, [tickets, dropdowns.statuses, formatTaskDate, t]);

  // Convert API tickets to task format and calculate pagination
  const tasks = React.useMemo(() => {
    const safeTickets = tickets || {};
    const safeStatuses = dropdowns?.statuses || [];

    const convertedTasks = Object.entries(safeTickets).flatMap(([statusName, statusData]) => {
      const safeStatusData = statusData || { tickets: [] };
      const safeTickets = safeStatusData.tickets || [];

      return safeTickets.map(ticket => {
        if (!ticket?.id) return null;
        // Keep raw due date for exact overdue checks; formatted value stays compact for the card.
        const hasDueTime = hasEffectiveDueTime(ticket.has_due_time, ticket.due_date);

        return {
          id: ticket.id,
          title: ticket.title || '',
          ticket_number: ticket.ticket_number || '',
          parent_ticket_number: ticket.parent_ticket_number || '',
          code: ticket.parent_ticket_number
            ? `${ticket.parent_ticket_number} > ${ticket.ticket_number || 0}`
            : `${ticket.ticket_number || 0}`,
          dueDate: formatTaskDate(ticket.due_date, ticket.due_time),
          dueDateRaw: ticket.due_date,
          hasDueTime,
          dueDateTooltip: formatDateWithOptionalTime(ticket.due_date, hasDueTime, { fallback: t('dashboard.noDueDate') }),
          priority: ticket?.priority,
          status: safeStatuses.find(s => s?.name === statusName)?.id || statusName,
          marked_as_pin: Boolean(ticket.marked_as_pin),
          assignee: {
            name: ticket.assignee || '',
            avatar: ticket.assignee ? ticket.assignee.slice(0, 2).toUpperCase() : 'UN'
          },
          assignees: ticket.assignees || [],
          project: ticket.project || '',
          sub_task_count: ticket.sub_task_count || 0,
          completed: ticket.marked_as_completed || ticket.completed || ticket.is_completed || false,
          labels: ticket.labels || [],
          estimateTime: minutesToTimeString(ticket.estimate_time_in_minutes),
          linked_tickets: ticket.linked_tickets || {}
        };
      }).filter(Boolean);
    });

    return convertedTasks;
  }, [tickets, dropdowns.statuses, formatTaskDate, t]);

  // Update selectedTask when tasks change (for completion state sync)
  useEffect(() => {
    if (selectedTask && tasks.length > 0) {
      const updatedTask = tasks.find(task => task.id === selectedTask.id);
      if (updatedTask && updatedTask.completed !== selectedTask.completed) {
        setSelectedTask({ ...selectedTask, completed: updatedTask.completed });
      }
    }
  }, [tasks, selectedTask]);

  // Auto-select newly loaded tasks when select all mode is active
  useEffect(() => {
    if (!isBulkMode || !bulkModeColumnId) return;

    const isSelectAllActive = selectAllModeByColumn[bulkModeColumnId];
    if (!isSelectAllActive) return;

    const columnTasks = tasksByStatusMap.get(bulkModeColumnId) || [];
    const currentSelected = selectedTasksByColumn[bulkModeColumnId] || new Set();
    const remainingSlots = 100 - currentSelected.size;

    if (remainingSlots <= 0) return;

    // Find tasks that are not yet selected
    const unselectedTasks = columnTasks.filter(task => !currentSelected.has(task.id));

    if (unselectedTasks.length > 0) {
      const tasksToSelect = unselectedTasks.slice(0, remainingSlots);
      const newSelected = new Set([...currentSelected, ...tasksToSelect.map(task => task.id)]);

      // Batch update to avoid multiple re-renders
      setSelectedTasksByColumn(prev => ({ ...prev, [bulkModeColumnId]: newSelected }));
    }
  }, [tasksByStatusMap, bulkModeColumnId, selectedTasksByColumn, selectAllModeByColumn, isBulkMode]);

  // Create task columns from API status data
  const taskColumns = (dropdowns?.statuses || []).map((status, index) => {
    const colors = [COLORS.status.open, COLORS.primary, COLORS.warning, COLORS.success, COLORS.secondary, COLORS.error];
    const icons = [Schedule, PlayArrow, RateReview, CheckCircle, Timeline, Flag];

    if (!status?.id || !status?.name) return null;

    // Determine count based on filters
    let count = 0;
    const safeTickets = tickets || {};
    const statusData = safeTickets[status.name] || { total: 0 };

    // Check if status filter is applied
    const isStatusFiltered = selectedStatus !== FILTER_VALUES.ALL;

    // If status filter is applied, show count only for selected status
    if (isStatusFiltered) {
      if (status.id === selectedStatus) {
        count = statusData.total || 0;
      } else {
        count = 0;
      }
    } else {
      // Always use total from API response (it reflects filtered results)
      count = statusData.total || 0;
    }

    return {
      id: status.id,
      title: status.name,
      count: count,
      icon: icons[index % icons.length],
      color: colors[index % colors.length],
      colour: status.colour || null,
      isDefault: status.status === 'DEFAULT'
    };
  }).filter(Boolean);

  // Throttle utility function
  const throttle = React.useCallback((func, delay) => {
    let timeoutId;
    let lastExecTime = 0;
    return function (...args) {
      const currentTime = Date.now();
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }, []);

  // Column-wise infinite scroll handler
  const loadMoreColumnData = React.useCallback(async (statusId) => {
    if (selectedProject === FILTER_VALUES.ALL) {
      return;
    }

    const columnState = columnPagination[statusId];
    if (!columnState || columnState.loading || !columnState.hasMore) {
      return;
    }

    const nextPage = columnState.page + 1;
    const filters = getCurrentFilters();

    try {
      await dispatch(fetchTicketsByColumn({
        projectId: selectedProject,
        statusId,
        filters,
        page: nextPage
      })).unwrap();
    } catch (error) {
      console.error('Column infinite scroll API call failed:', error);
    }
  }, [selectedProject, columnPagination, dispatch, getCurrentFilters]);

  // Throttled version of loadMoreColumnData
  const throttledLoadMoreColumnData = React.useMemo(
    () => throttle(loadMoreColumnData, 2000),
    [throttle, loadMoreColumnData]
  );

  // Setup scroll listeners for each column
  React.useEffect(() => {
    if (viewMode !== VIEW_MODES.BOARD || dropdowns.statuses.length === 0) return;

    const listeners = new Map();
    const scrollingClasses = new Map();

    const handleScroll = (columnId) => (e) => {
      const element = e.target;
      const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 5;

      // Hide all tooltips immediately
      document.querySelectorAll('.MuiTooltip-popper').forEach(tooltip => {
        tooltip.style.display = 'none';
      });

      // Add scrolling class for tooltip hiding
      element.classList.add('scrolling');

      // Clear existing timeout for this element
      if (scrollingClasses.has(columnId)) {
        clearTimeout(scrollingClasses.get(columnId));
      }

      // Set timeout to remove scrolling class
      const timeoutId = setTimeout(() => {
        element.classList.remove('scrolling');
        scrollingClasses.delete(columnId);
        // Restore tooltips
        document.querySelectorAll('.MuiTooltip-popper').forEach(tooltip => {
          tooltip.style.display = '';
        });
      }, 150);

      scrollingClasses.set(columnId, timeoutId);

      if (isAtBottom) {
        throttledLoadMoreColumnData(columnId);
      }
    };

    // Setup listeners once DOM is ready
    const timer = setTimeout(() => {
      dropdowns.statuses.forEach(status => {
        const element = columnScrollRefs.current[status.id];
        if (element) {
          const listener = handleScroll(status.id);
          element.addEventListener('scroll', listener);
          listeners.set(status.id, { element, listener });
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      listeners.forEach(({ element, listener }) => {
        element.removeEventListener('scroll', listener);
      });
      listeners.clear();
      // Clear all scrolling timeouts
      scrollingClasses.forEach(timeoutId => clearTimeout(timeoutId));
      scrollingClasses.clear();
    };
  }, [throttledLoadMoreColumnData, dropdowns.statuses, viewMode]);

  // Setup scroll listener for main content area (horizontal scrolling)
  React.useEffect(() => {
    const contentArea = document.querySelector('.content-area');
    if (!contentArea) return;

    let scrollTimeout;

    const handleContentScroll = () => {
      contentArea.classList.add('scrolling');

      // Hide all tooltips immediately
      document.querySelectorAll('.MuiTooltip-popper').forEach(tooltip => {
        tooltip.style.display = 'none';
      });

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        contentArea.classList.remove('scrolling');
        // Restore tooltips
        document.querySelectorAll('.MuiTooltip-popper').forEach(tooltip => {
          tooltip.style.display = '';
        });
      }, 150);
    };

    contentArea.addEventListener('scroll', handleContentScroll, { passive: true });

    return () => {
      contentArea.removeEventListener('scroll', handleContentScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [viewMode]);
  const getStatusColor = (statusId) => {
    const status = (dropdowns?.statuses || []).find(s => s.id === statusId);
    if (!status) return COLORS.gray[800];

    // 1. Solid color from API (Strip alpha if present)
    if (status.colour && status.colour !== 'null') {
      return status.colour.length > 7 ? status.colour.substring(0, 7) : status.colour;
    }

    // 2. Map status names to predefined colors
    const statusName = status.name.toLowerCase();
    if (statusName.includes('todo') || statusName.includes('open')) return COLORS.status.open;
    if (statusName.includes('progress') || statusName.includes('doing')) return COLORS.status.inProgress;
    if (statusName.includes('review')) return COLORS.status.review;
    if (statusName.includes('done') || statusName.includes('closed') || statusName.includes('complete')) return COLORS.status.done;
    if (statusName.includes('blocked')) return COLORS.status.blocked;

    // 3. Last fallback (Match getColumnTheme but solid)
    const themeColor = getColumnTheme(status.name);
    if (themeColor && themeColor.startsWith('#')) {
      return themeColor.substring(0, 7);
    }

    return COLORS.gray[800];
  };

  const getStatusLabel = (statusId) => {
    const status = dropdowns.statuses.find(s => s.id === statusId);
    return status ? status.name : t('dashboard.unknown');
  };

  // Helper function to get display name
  const getDisplayName = (value, type = 'string') => {
    if (typeof value === 'object' && value.name) {
      return value.name;
    }
    return value || t('dashboard.notSet');
  };

  // Memoized overdue calculations to prevent date parsing on every render
  const overdueMap = useMemo(() => {
    return tasks.reduce((acc, task) => {
      if (!task.dueDateRaw || task.dueDate === t('dashboard.noDueDate') || task.completed) {
        acc[task.id] = false;
      } else {
        acc[task.id] = isDueDateOverdue(task.dueDateRaw, task.hasDueTime, task.completed);
      }
      return acc;
    }, {});
  }, [tasks, t]);

  const copyTaskLink = React.useCallback((task) => handleCopyTaskLink(task, t), [t]);
  const copyTaskTitle = React.useCallback((task) => handleCopyTaskTitle(task, t), [t]);

  // Function to clear all filters while preserving selected project
  const handleClearFilters = React.useCallback(() => {
    const clearedSprintSelection = isSelectedProjectScrum ? [SCRUM_BACKLOG_SENTINEL] : [FILTER_VALUES.ALL];
    setSearchTerm('');
    if (searchInputRef.current) searchInputRef.current.value = '';
    searchTermRef.current = '';
    setSelectedPriority(FILTER_VALUES.ALL);
    setSelectedAssignee(FILTER_VALUES.ALL);
    setSelectedCreatedBy(FILTER_VALUES.ALL);
    setSelectedTaskType(FILTER_VALUES.ALL);
    setSelectedStatus(FILTER_VALUES.ALL);
    setSelectedLabels([FILTER_VALUES.ALL]);
    setSelectedSprint(clearedSprintSelection);
    setSelectedEpic(FILTER_VALUES.ALL); // Clear epic filter
    setStartDate('');
    setDueDate('');
    setSelectedSort('NEWEST_FIRST');

    dispatch(setFilters({
      searchTerm: '',
      selectedProject: selectedProject,
      selectedPriority: FILTER_VALUES.ALL,
      selectedAssignee: FILTER_VALUES.ALL,
      selectedCreatedBy: FILTER_VALUES.ALL,
      selectedTaskType: FILTER_VALUES.ALL,
      selectedStatus: FILTER_VALUES.ALL,
      selectedLabels: [FILTER_VALUES.ALL],
      selectedSprint: clearedSprintSelection,
      selectedEpic: FILTER_VALUES.ALL, // Clear epic filter in Redux
      startDate: '',
      dueDate: '',
      selectedSort: 'NEWEST_FIRST',
      epicStartDate: null, // Clear epic start date
      epicDueDate: null    // Clear epic due date
    }));

    if (selectedProject !== FILTER_VALUES.ALL) {
      const clearFilters = isSelectedProjectScrum ? { scrum_sprint_id: SCRUM_BACKLOG_SENTINEL } : {};
      const fetchKey = JSON.stringify({ projectId: selectedProject, filters: clearFilters, page: 1, append: false });
      lastFetchKeyRef.current = fetchKey;
      dispatch(fetchTicketsByProject({
        projectId: selectedProject,
        filters: clearFilters,
        page: 1,
        append: false
      }));
    }
  }, [selectedProject, dispatch, isSelectedProjectScrum]);

  // Refresh data function
  const refreshCurrentTickets = React.useCallback(async () => {
    if (selectedProject !== FILTER_VALUES.ALL) {
      const fetchKey = JSON.stringify({ projectId: selectedProject, filters: getCurrentFilters(), page: 1, append: false });
      lastFetchKeyRef.current = fetchKey;
      await dispatch(fetchTicketsByProject({
        projectId: selectedProject,
        filters: getCurrentFilters(),
        page: 1,
        append: false
      }));
    }
  }, [selectedProject, dispatch, getCurrentFilters]);

  const handleRefresh = React.useCallback(async () => {
    if (selectedProject !== FILTER_VALUES.ALL) {
      setIsRefreshing(true);
      try {
        await refreshCurrentTickets();
      } finally {
        setIsRefreshing(false);
      }
    }
  }, [selectedProject, refreshCurrentTickets]);

  const handleNotificationsClick = () => {
    navigate('/notifications', { state: { from: location.pathname } });
  };

  // Common function to open add task modal
  const handleOpenAddTaskModal = React.useCallback((statusId = null, modalOpen = true, selectedDate = null) => {
    const projectStatus = selectedProjectDetails?.status;
    if (
      projectStatus === PROJECT_STATUS.ON_HOLD ||
      projectStatus === PROJECT_STATUS.INACTIVE ||
      projectStatus === PROJECT_STATUS.COMPLETED
    ) {
      pendingProjectReactivationActionRef.current = null;
      setProjectStatusModalContent(null);
      setProjectStatusForModal(projectStatus);
      setIsProjectStatusModalOpen(true);
      return;
    }
    setPreselectedStatusId(statusId);
    setSelectedDateForTask(selectedDate);
    setIsAddTaskModalOpen(modalOpen);
  }, [selectedProjectDetails]);

  const handleActivateProject = async () => {
    const pendingAction = pendingProjectReactivationActionRef.current;
    const projectId = pendingAction?.projectId || selectedProject;
    const previousStatus = projectStatusForModal;
    if (!projectId || projectId === FILTER_VALUES.ALL) return;

    setActivatingProject(true);
    dispatch(setProjectDropdownStatus({ projectId, status: PROJECT_STATUS.ACTIVE }));

    try {
      await dispatch(updateProject({
        id: projectId,
        data: { status: PROJECT_STATUS.ACTIVE }
      })).unwrap();

      toast.success(t('projects.projectUpdatedSuccess'));
      closeProjectStatusModal();
      if (pendingAction?.onConfirm) {
        await pendingAction.onConfirm();
      }
    } catch (error) {
      console.error('Failed to activate project:', error);
      if (previousStatus) {
        dispatch(setProjectDropdownStatus({ projectId, status: previousStatus }));
      }
      toast.error(t('projects.projectUpdateFailed'));
    } finally {
      setActivatingProject(false);
    }
  };

  // Fullscreen functionality
  const handleToggleFullscreen = React.useCallback(() => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);

    // Add/remove fullscreen class to body
    if (newFullscreenState) {
      document.body.classList.add('dashboard-fullscreen-mode');
    } else {
      document.body.classList.remove('dashboard-fullscreen-mode');
    }
  }, [isFullscreen]);

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        handleToggleFullscreen();
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isFullscreen, handleToggleFullscreen]);

  // Cleanup fullscreen mode on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('dashboard-fullscreen-mode');
    };
  }, []);

  /**
   * Task drag and drop handlers for board view
   * Handles moving tasks between status columns
   */
  // Visual feedback manager for task drag operations
  const taskVisualManager = useMemo(() => createDragVisualManager({
    classNames: ['drag-over-column'],
    dataAttribute: 'data-column-id'
  }), []);

  const handleDragStart = React.useCallback((e, task) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);

    // Create and set custom drag image
    const dragElement = createDragImage(e.currentTarget, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    e.dataTransfer.setDragImage(dragElement, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    requestAnimationFrame(() => document.body.removeChild(dragElement));

    requestAnimationFrame(() => setDraggedTask(task));
  }, []);

  const handleDragEnd = React.useCallback(() => {
    lastColumnRef.current = null;
    localDragOverRef.current = null;
    taskVisualManager.clear();
    setDraggedTask(null);
    setDragOverColumn(null);
  }, [taskVisualManager]);

  const lastColumnRef = React.useRef(null);
  const localDragOverRef = React.useRef(null);

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (lastColumnRef.current !== columnId) {
      lastColumnRef.current = columnId;
      localDragOverRef.current = columnId;
      taskVisualManager.apply(columnId, 'drag-over-column');
    }
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      localDragOverRef.current = null;
      taskVisualManager.clear();
    }
  };

  const performTaskMove = React.useCallback(async (task, columnId) => {
    if (!task || !columnId || task.status === columnId) return;

    const targetColumn = taskColumns.find(col => col.id === columnId);
    const taskTitle = task.title;

    // Optimistic update - immediately move task in UI
    const oldStatusName = dropdowns.statuses.find(s => s.id === task.status)?.name;
    const newStatusName = dropdowns.statuses.find(s => s.id === columnId)?.name;

    if (oldStatusName && newStatusName) {
      dispatch(updateTicketStatus({
        ticketId: task.id,
        oldStatus: { name: oldStatusName },
        newStatus: { name: newStatusName }
      }));
    }

    try {
      await dispatch(updateTicketPartial({
        ticketId: task.id,
        ticketData: { status_id: columnId }
      })).unwrap();

      toast.success(t('dashboard.taskMovedSuccess', { task: taskTitle, column: targetColumn?.title || 'Unknown' }));
    } catch (error) {
      console.error('Failed to update task status:', error);
      toast.error(t('dashboard.taskMovedError', { task: taskTitle }));

      // Revert optimistic update on error
      if (oldStatusName && newStatusName) {
        dispatch(updateTicketStatus({
          ticketId: task.id,
          oldStatus: { name: newStatusName },
          newStatus: { name: oldStatusName }
        }));
      }
    }
  }, [dispatch, dropdowns.statuses, t, taskColumns]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    localDragOverRef.current = null;
    taskVisualManager.clear();
    setDragOverColumn(null);

    if (!draggedTask || draggedTask.status === columnId) {
      setDraggedTask(null);
      return;
    }

    setPendingMove({ task: draggedTask, columnId });
    setIsMoveConfirmOpen(true);
    setDraggedTask(null);
  };

  const handleCancelMoveTask = () => {
    setIsMoveConfirmOpen(false);
    setPendingMove(null);
  };

  const handleConfirmMoveTask = async () => {
    if (!pendingMove?.task || !pendingMove?.columnId) {
      handleCancelMoveTask();
      return;
    }

    const move = pendingMove;
    setIsMoveConfirmOpen(false);
    setPendingMove(null);
    setIsMoveConfirmLoading(true);
    try {
      await performTaskMove(move.task, move.columnId);
    } finally {
      setIsMoveConfirmLoading(false);
    }
  };

  const dragPositionRef = React.useRef(null);

  // Status drag and drop functions
  const handleStatusDragStart = React.useCallback((e, status) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', status.id);

    // Create and set custom drag image with clamped offsets
    const offsetX = Math.max(0, e.nativeEvent.offsetX - 200);
    const offsetY = Math.max(0, e.nativeEvent.offsetY);
    const dragElement = createDragImage(e.currentTarget, offsetX, offsetY);
    e.dataTransfer.setDragImage(dragElement, offsetX, offsetY);
    requestAnimationFrame(() => document.body.removeChild(dragElement));

    // Only store serializable data in Redux
    const serializableStatus = {
      id: status.id,
      title: status.title,
      count: status.count,
      isDefault: status.isDefault
    };
    dispatch(setDraggedStatus(serializableStatus));
  }, [dispatch]);

  /**
   * Cleans up after status drag operation ends
   * Removes visual indicators and resets drag state
   */
  const handleStatusDragEnd = React.useCallback(() => {
    dragPositionRef.current = null;
    // Clean up all drag visual indicators
    document.querySelectorAll('.drag-left, .drag-right, .drag-left-ghost, .drag-right-ghost').forEach(el => {
      el.classList.remove('drag-left', 'drag-right', 'drag-left-ghost', 'drag-right-ghost');
    });
    dispatch(setDraggedStatus(null));
  }, [dispatch]);


  /**
   * Reorders status array and syncs with server
   */
  const performStatusReorder = React.useCallback((draggedIndex, targetIndex, position) => {
    if (draggedIndex === targetIndex) return;

    const reorderedStatuses = [...dropdowns.statuses];
    const [draggedItem] = reorderedStatuses.splice(draggedIndex, 1);

    // Calculate insertion index based on position
    let insertIndex = targetIndex;

    // For board view, adjust based on position (left/right)
    if (position === 'left') {
      insertIndex = targetIndex;
    } else if (position === 'right') {
      insertIndex = targetIndex + 1;
    } else {
      // For list view, targetIndex is already calculated correctly
      insertIndex = targetIndex;
    }

    // Adjust for removal if dragged item was before insertion point
    if (draggedIndex < insertIndex) {
      insertIndex--;
    }

    // Insert at calculated position
    reorderedStatuses.splice(insertIndex, 0, draggedItem);

    dispatch(reorderStatuses({ statuses: reorderedStatuses, previousOrder: dropdowns.statuses }));

    const statusOrder = reorderedStatuses.map(s => s.id);
    dispatch(updateStatusOrder({ projectId: selectedProject, statusOrder }))
      .unwrap()
      .then(() => toast.success(t('dashboard.statusOrderUpdatedSuccess')))
      .catch(() => {
        toast.error(t('dashboard.statusOrderUpdateError'));
        dispatch(reorderStatuses({ statuses: dropdowns.statuses, previousOrder: reorderedStatuses }));
      });
  }, [dropdowns.statuses, selectedProject, dispatch, t]);

  // Board column drag handlers using centralized utilities
  const boardColumnDragHandlers = React.useMemo(() => createBoardColumnDragHandlers({
    draggedStatus,
    canReorderStatus,
    statuses: dropdowns.statuses,
    performStatusReorder,
    dispatch,
    setDraggedStatus: (status) => dispatch(setDraggedStatus(status)),
    dragPositionRef
  }), [draggedStatus, canReorderStatus, dropdowns.statuses, performStatusReorder, dispatch]);

  const { dragOver: handleColumnDragOver, dragLeave: handleColumnDragLeave, drop: handleColumnDrop } = boardColumnDragHandlers;



  const onBoardDragOver = React.useMemo(() => createAutoScroll(100, 30, true), []);
  const onContentAreaDragOver = React.useMemo(() => createAutoScroll(100, 20, false), []);


  // Multiselect handlers - per column with optimized Set operations and 100 task limit
  const handleTaskSelect = React.useCallback((taskId) => {
    if (!bulkModeColumnId) return;

    setSelectedTasksByColumn(prev => {
      const currentSet = prev[bulkModeColumnId] || new Set();
      const newSet = new Set(currentSet);

      if (newSet.has(taskId)) {
        newSet.delete(taskId);
        // If user manually deselects a task, disable select all mode
        setSelectAllModeByColumn(prevMode => ({ ...prevMode, [bulkModeColumnId]: false }));
      } else {
        if (newSet.size >= 100) {
          return prev; // Don't add if already at limit
        }
        newSet.add(taskId);
      }

      return { ...prev, [bulkModeColumnId]: newSet };
    });
  }, [bulkModeColumnId]);

  const handleSelectAllInColumn = React.useCallback(() => {
    if (!bulkModeColumnId) return;

    const columnTasks = tasksByStatusMap.get(bulkModeColumnId) || [];
    const currentSelected = selectedTasksByColumn[bulkModeColumnId] || new Set();
    const remainingSlots = 100 - currentSelected.size;

    if (remainingSlots <= 0) return;

    const unselectedTasks = columnTasks.filter(task => !currentSelected.has(task.id));
    const tasksToSelect = unselectedTasks.slice(0, remainingSlots);
    const newSelected = new Set([...currentSelected, ...tasksToSelect.map(task => task.id)]);

    setSelectedTasksByColumn(prev => ({ ...prev, [bulkModeColumnId]: newSelected }));
    // Mark this column as having select all mode active
    setSelectAllModeByColumn(prev => ({ ...prev, [bulkModeColumnId]: true }));
  }, [bulkModeColumnId, selectedTasksByColumn, tasksByStatusMap]);

  const handleDeselectAllInColumn = React.useCallback(() => {
    if (!bulkModeColumnId) return;

    setSelectedTasksByColumn(prev => ({ ...prev, [bulkModeColumnId]: new Set() }));
    // Disable select all mode for this column
    setSelectAllModeByColumn(prev => ({ ...prev, [bulkModeColumnId]: false }));
  }, [bulkModeColumnId]);

  const enterBulkMode = React.useCallback((columnId) => {
    setBulkModeColumnId(columnId);
    // Clear any existing selections
    setSelectedTasksByColumn({});
    // Clear select all mode states
    setSelectAllModeByColumn({});
  }, []);



  const handleKebabMenuItemClick = React.useCallback((item) => {
    const column = taskColumns.find(col => col.id === kebabMenu.menuId);

    switch (item.action) {
      case 'edit':
        if (column && !column.isDefault) {
          handleEditColumn(column);
        }
        break;
      case 'delete':
        if (column && !column.isDefault) {
          handleDeleteColumn(column);
        }
        break;
      case 'bulk':
        enterBulkMode(kebabMenu.menuId);
        break;
      case 'bulk-edit':
        handleBulkEdit();
        break;
      case 'bulk-delete':
        handleBulkDelete();
        break;
      case 'bulk-exit':
        exitBulkMode();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kebabMenu.menuId, taskColumns, handleEditColumn, handleDeleteColumn, enterBulkMode]);

  const getKebabMenuItems = React.useMemo(() => {
    const currentMenuId = kebabMenu.menuId;
    const column = taskColumns.find(col => col.id === currentMenuId);
    if (!column) return [];

    const isColumnInBulkMode = bulkModeColumnId === currentMenuId;
    const selectedCount = selectedTasksByColumn[currentMenuId]?.size || 0;

    const items = [];

    // Edit Status Name
    if (canManageStatus && !column.isDefault) {
      items.push({
        id: 'edit',
        label: 'dashboard.bulkActions.editStatus',
        action: 'edit',
        disabled: isColumnInBulkMode,
      });
    }

    // Delete Status
    if (canManageStatus && !column.isDefault) {
      items.push({
        id: 'delete',
        label: 'dashboard.bulkActions.deleteStatus',
        action: 'delete',
        color: 'red',
        disabled: isColumnInBulkMode,
      });
    }

    // Bulk Actions Item (Expandable)
    items.push({
      ...BULK_ACTION_ITEMS.BULK_ACTIONS,
      id: 'bulk-actions-expander',
      labelNode: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {t('dashboard.bulkActions.actions')}
          {isColumnInBulkMode && selectedCount > 0 && (
            <span className="kebab-bulk-count">{selectedCount}</span>
          )}
        </span>
      ),
      label: 'dashboard.bulkActions.actions',
      stayOpen: true,
      isSelected: isColumnInBulkMode,
      disabled: !tasksByStatusMap.get(currentMenuId)?.length,
      children: [
        {
          id: 'bulk-edit',
          label: 'dashboard.bulkActions.edit',
          action: 'bulk-edit',
          color: 'blue',
          stayOpen: true,
          disabled: !selectedTasksByColumn[currentMenuId]?.size
        },
        {
          id: 'bulk-delete',
          label: 'dashboard.bulkActions.delete',
          action: 'bulk-delete',
          color: 'red',
          stayOpen: true,
          disabled: !selectedTasksByColumn[currentMenuId]?.size,
          hidden: user?.role === USER_ROLES.USER
        },
        {
          id: 'bulk-exit',
          label: 'dashboard.bulkActions.exitMode',
          action: 'bulk-exit',
          sx: {
            color: '#d32f2f',
            fontWeight: 400,
            '&:hover': { color: '#b71c1c', bgcolor: '#ffebee' }
          }
        }
      ].filter(c => !c.hidden)
    });

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskColumns, kebabMenu.menuId, tasksByStatusMap, canManageStatus, selectedTasksByColumn, bulkModeColumnId, user?.role, t]);

  const handleBulkEdit = React.useCallback(() => {
    const selectedTasks = selectedTasksByColumn[bulkModeColumnId];
    if (selectedTasks && selectedTasks.size > 0) {
      setBulkEditColumnId(bulkModeColumnId);
      setIsBulkEditModalOpen(true);
    }
  }, [selectedTasksByColumn, bulkModeColumnId]);

  // Memoized selected tasks for modals
  const bulkEditSelectedTasks = useMemo(() => {
    if (!bulkEditColumnId) return [];
    const sel = selectedTasksByColumn[bulkEditColumnId];
    if (!sel || sel.size === 0) return [];
    return tasks.filter(task => sel.has(task.id));
  }, [tasks, selectedTasksByColumn, bulkEditColumnId]);

  const bulkDeleteSelectedTasks = useMemo(() => {
    if (!bulkDeleteColumnId) return [];
    const sel = selectedTasksByColumn[bulkDeleteColumnId];
    if (!sel || sel.size === 0) return [];
    return tasks.filter(task => sel.has(task.id));
  }, [tasks, selectedTasksByColumn, bulkDeleteColumnId]);

  const handleBulkDelete = React.useCallback(() => {
    const selectedTasks = selectedTasksByColumn[bulkModeColumnId];
    if (selectedTasks && selectedTasks.size > 0) {
      setBulkDeleteColumnId(bulkModeColumnId);
      setIsBulkDeleteModalOpen(true);
    }
  }, [selectedTasksByColumn, bulkModeColumnId]);

  const handleBulkDeleteConfirm = React.useCallback(async () => {
    setBulkDeleteLoading(true);
    try {
      const selectedTasksArray = selectedTaskArrays[bulkDeleteColumnId];

      if (!selectedTasksArray || selectedTasksArray.length === 0) {
        toast.error(t('dashboard.bulkActions.toast.noTasksSelectedDelete'));
        return;
      }

      // Call bulk delete API
      const response = await dispatch(bulkDeleteTickets({
        projectId: selectedProject,
        taskIds: selectedTasksArray
      })).unwrap();

      // Show toast based on API response
      if (response?.response?.categories?.delete) {
        const { success, failed } = response.response.categories.delete;
        if (failed > 0) {
          toast.warning(t('dashboard.bulkActions.toast.bulkDeleteResults', { success, failed }));
        } else {
          toast.success(t(success !== 1 ? 'dashboard.bulkActions.toast.bulkDeleteSuccess_plural' : 'dashboard.bulkActions.toast.bulkDeleteSuccess', { success }));
        }
      } else {
        toast.success(t(selectedTasksArray.length !== 1 ? 'dashboard.bulkActions.toast.bulkDeleteSuccess_plural' : 'dashboard.bulkActions.toast.bulkDeleteSuccess', { success: selectedTasksArray.length }));
      }

      // Refresh tickets only if component is still mounted
      if (isMountedRef.current && selectedProject && selectedProject !== FILTER_VALUES.ALL) {
        dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error(t('dashboard.bulkActions.toast.bulkDeleteFailed'));
    } finally {
      setBulkDeleteLoading(false);
      // Close modal and clear state after API response
      exitBulkMode();
      setBulkDeleteColumnId(null);
      setIsBulkDeleteModalOpen(false);
    }
  }, [selectedTaskArrays, bulkDeleteColumnId, selectedProject, dispatch, getCurrentFilters, t]);

  const handleBulkEditSubmit = React.useCallback(async (updates) => {
    setBulkEditLoading(true);
    try {
      const selectedTasksArray = selectedTaskArrays[bulkEditColumnId];

      if (!selectedTasksArray || selectedTasksArray.length === 0) {
        toast.error(t('dashboard.bulkActions.toast.noTasksSelected'));
        return;
      }

      // Call bulk update API
      const response = await dispatch(bulkUpdateTickets({
        projectId: selectedProject,
        taskIds: selectedTasksArray,
        updates
      })).unwrap();

      // Format toast message based on API response
      if (response?.response?.categories) {
        const categories = response.response.categories;

        const categoryEntries = Object.entries(categories).map(([key, cat]) => {
          let name = key;
          if (key === 'change_status') name = t('dashboard.bulkActions.toast.categoryStatus');
          else if (key === 'assign') name = t('dashboard.bulkActions.toast.categoryAssignee');
          else if (key === 'change_due_date') name = t('dashboard.bulkActions.toast.categoryDueDate');
          else if (key === 'add_comment') name = t('dashboard.bulkActions.toast.categoryComments');
          return { name, success: cat.success, failed: cat.failed };
        });

        const hasFailures = Object.values(categories).some(cat => cat.failed > 0);

        const ToastContent = () => (
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{t('dashboard.bulkActions.toast.bulkEditResults', { total: response.response.total || selectedTasksArray.length })}</div>
            {categoryEntries.map((cat, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                {t('dashboard.bulkActions.toast.categoryResult', { name: cat.name, success: cat.success, failed: cat.failed })}
              </div>
            ))}
          </div>
        );

        if (hasFailures) {
          toast.warning(<ToastContent />, {
            autoClose: 5000
          });
        } else {
          toast.success(<ToastContent />, {
            autoClose: 5000
          });
        }
      } else {
        toast.success(t('dashboard.bulkActions.toast.bulkUpdateSuccess'));
      }

      // Refresh tickets only if component is still mounted
      if (isMountedRef.current && selectedProject && selectedProject !== FILTER_VALUES.ALL) {
        dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
      }
    } catch (error) {
      console.error('Bulk update failed:', error);
      const errorMessage = error.message || t('dashboard.bulkActions.toast.bulkUpdateFailed');
      toast.error(errorMessage);
    } finally {
      setBulkEditLoading(false);
      // Close modal and clear state after API response
      exitBulkMode();
      setBulkEditColumnId(null);
      setIsBulkEditModalOpen(false);
    }
  }, [selectedTaskArrays, bulkEditColumnId, selectedProject, dispatch, getCurrentFilters, t]);

  // Stable callbacks for TaskCard to ensure memoization works
  const stableHandleEdit = React.useCallback((task) => handleEdit(task), [handleEdit]);
  const stableHandleDelete = React.useCallback((task) => handleDelete(task), [handleDelete]);
  const stableCopyTaskLink = React.useCallback((task) => copyTaskLink(task), [copyTaskLink]);
  const stableCopyTaskTitle = React.useCallback((task) => copyTaskTitle(task), [copyTaskTitle]);
  const stableHandleViewTask = React.useCallback((task) => handleViewTask(task), [handleViewTask]);
  const stableHandleDragStart = React.useCallback((e, task) => handleDragStart(e, task), [handleDragStart]);
  const stableHandleDragEnd = React.useCallback(() => handleDragEnd(), [handleDragEnd]);

  // Link handlers
  const handleViewLinks = React.useCallback((task) => {
    setViewingLinksTask(task);
    setIsViewLinksModalOpen(true);
  }, []);

  const handleAddLink = React.useCallback((task) => {
    setLinkingTask(task);
    setIsLinkModalOpen(true);
  }, []);

  // Memoized style objects to prevent re-renders
  const taskCardStyles = useMemo(() => ({
    base: {
      cursor: 'grab',
      transition: 'all 0.2s ease-in-out',
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none'
      },
      '&:active': {
        cursor: 'grabbing'
      },
      borderRadius: 3,
      '@media (max-width: 1380px)': {
        borderRadius: 2
      },
      position: 'relative',
      width: '100%',
      maxWidth: { xs: 280, sm: 300, md: 320 },
      zIndex: 'auto'
    },
    iconButton: {
      p: 0.25
    },
    editHover: {
      '&:hover': {
        bgcolor: COLORS.blue[50],
        '& .edit-icon': {
          color: COLORS.blue[600]
        }
      }
    },
    deleteHover: {
      '&:hover': {
        bgcolor: COLORS.red[50],
        '& .delete-icon': {
          color: COLORS.priority.critical
        }
      }
    },
    linkHover: {
      '&:hover': {
        bgcolor: COLORS.gray[50],
        '& .link-icon': {
          color: COLORS.gray[600]
        }
      }
    }
  }), []);

  const TaskCard = React.memo(({ task, onDragStart, onDragEnd, isDragging, columnId, isNewlyCreated }) => {
    const cardRef = useRef(null);

    useEffect(() => {
      if (isNewlyCreated && cardRef.current) {
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, [isNewlyCreated]);

    const isOverdue = overdueMap[task.id];
    const isColumnInBulkMode = bulkModeColumnId === columnId;
    const isSelected = selectedTaskSets[bulkModeColumnId]?.has(task.id) || false;

    const handleEditClick = React.useCallback((e) => {
      e.stopPropagation();
      stableHandleEdit(task);
    }, [task]);

    const handleDeleteClick = React.useCallback((e) => {
      e.stopPropagation();
      stableHandleDelete(task);
    }, [task]);

    const handleCopyClick = React.useCallback((e) => {
      e.stopPropagation();
      stableCopyTaskLink(task);
    }, [task]);

    const handleCopyTitleClick = React.useCallback((e) => {
      e.stopPropagation();
      stableCopyTaskTitle(task);
    }, [task]);

    const handleTogglePinClick = React.useCallback((e) => {
      e.stopPropagation();
      setAnchorEl(null);
      const ticketLabel = task.ticket_number || task.code || task.id;
      const nextPinned = !task.marked_as_pin;

      dispatch(optimisticMergeTicket({
        ticketId: task.id,
        updates: { marked_as_pin: nextPinned }
      }));

      dispatch(updateTicketPartial({
        ticketId: task.id,
        ticketData: {
          marked_as_pin: nextPinned
        }
      })).unwrap()
        .then(() => {
          toast.success(nextPinned
            ? t('dashboard.ticketPinnedSuccess', '{{ticketLabel}} pinned successfully', { ticketLabel })
            : t('dashboard.ticketUnpinnedSuccess', '{{ticketLabel}} unpinned successfully', { ticketLabel }));
        })
        .catch((error) => {
          dispatch(optimisticMergeTicket({
            ticketId: task.id,
            updates: { marked_as_pin: task.marked_as_pin }
          }));
          console.error('Failed to toggle pin state:', error);
          toast.error(t('dashboard.ticketPinUpdateFailed', 'Failed to update ticket pin status'));
        });
    }, [task]);

    const handleTaskSelectClick = React.useCallback((e) => {
      e.stopPropagation();
      handleTaskSelect(task.id);
    }, [task.id]);

    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const handleMenuOpen = (e) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = (e) => {
      if (e) e.stopPropagation();
      setAnchorEl(null);
    };

    // Dynamic styles that depend on task state
    const dynamicCardStyles = useMemo(() => ({
      ...taskCardStyles.base,
      cursor: isColumnInBulkMode ? 'pointer' : (loadingTaskId === task.id ? 'wait' : 'grab'),
      opacity: loadingTaskId === task.id ? 0.7 : ((!isColumnInBulkMode && isDragging) ? 0.3 : 1),
      filter: (!isColumnInBulkMode && isDragging) ? 'grayscale(50%)' : 'none',

      '&:hover': {
        ...taskCardStyles.base['&:hover'],
        transform: loadingTaskId === task.id ? 'none' : ((!isColumnInBulkMode && isDragging) ? 'scale(0.95)' : 'translateY(-1px)')
      },
      border: isNewlyCreated
        ? `2px solid ${COLORS.primary}`
        : `1px solid ${isSelected ? COLORS.primary : (isOverdue ? COLORS.red[300] : COLORS.gray[200])}`,
      bgcolor: isNewlyCreated
        ? UI.newTaskHighlight
        : (isSelected ? COLORS.blue[50] : (isOverdue ? COLORS.red[50] : COLORS.white)),
      boxShadow: isNewlyCreated
        ? `0 0 0 3px rgba(37, 99, 235, ${UI.shadowOpacity.medium}), 0 4px 12px rgba(37, 99, 235, ${UI.shadowOpacity.light})`
        : (isSelected ? `0 2px 8px rgba(59, 130, 246, ${UI.shadowOpacity.light})` : taskCardStyles.base.boxShadow),
      transition: `all ${UI.animationMs.highlight}ms ease`,
      transform: ((!isColumnInBulkMode && isDragging) ? 'scale(0.95)' : 'scale(1)')
    }), [task.id, isDragging, isOverdue, isSelected, isColumnInBulkMode, isNewlyCreated]);

    return (
      <Card
        ref={cardRef}
        draggable={!isColumnInBulkMode}
        onDragStart={
          !isColumnInBulkMode
            ? (e) => {
              if (onDragStart) onDragStart(e, task);

              const element = e.currentTarget;
              if (!element) return;

              // Add cleanup for drag end
              const handleDragEndCleanup = () => {
                const container = element?.closest('.scrollable-column-content') || element?.closest('.content-area');
                if (container) {
                  container.querySelectorAll('.task-card-dragging').forEach((el) => {
                    el.classList.remove('task-card-dragging');
                  });
                }
                if (onDragEnd) onDragEnd();

              };

              element.addEventListener('dragend', handleDragEndCleanup, { once: true });
            }
            : undefined
        }
        onClick={isColumnInBulkMode ? handleTaskSelectClick : (e) => {
          if (!e.defaultPrevented && loadingTaskId !== task.id) {
            stableHandleViewTask(task);
          }
        }}
        className={`${(!isColumnInBulkMode && isDragging) ? 'task-card-dragging' : ''} ${isOverdue ? 'task-card-overdue' : ''}`}
        sx={dynamicCardStyles}
      >
        <CardContent sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          '@media (max-width: 1380px)': {
            p: 1.5,
            '&:last-child': { pb: 1.5 }
          }
        }}>

          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 0.75, lg: 1 }}>
            <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ minWidth: 0, flex: 1, width: 0 }}>
              {isColumnInBulkMode && (
                <Tooltip title={isSelected ? t('dashboard.bulkActions.deselectTask') : (isSelectionLimitReached ? t('dashboard.bulkActions.selectionLimitReached') : t('dashboard.bulkActions.selectTask'))} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleTaskSelectClick}
                      className="task-select-btn"
                      sx={{ p: 0.5 }}
                      aria-label={isSelected ? t('dashboard.bulkActions.deselectTask') : t('dashboard.bulkActions.selectTask')}
                      disabled={!isSelected && isSelectionLimitReached}
                    >
                      {isSelected ? (
                        <CheckBox className="task-checkbox-selected" sx={{ fontSize: 20 }} />
                      ) : (
                        <CheckBoxOutlineBlank className="task-checkbox-unselected" sx={{ fontSize: 20, opacity: (!isSelected && isSelectionLimitReached) ? 0.5 : 1 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!isColumnInBulkMode && (
                <TaskCompletionCheckbox
                  task={task}
                  size="small"
                  disabled={loadingTaskId === task.id}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <OverflowTooltip
                  title={task.title}
                  tooltipWidth={300}
                  variant="body2"
                  className="task-title"
                  maxLines={2}
                  sx={{
                    color: COLORS.text.primary,
                    minWidth: 0,
                    flex: 1
                  }}
                  placement="bottom-start"
                >
                  {isNewlyCreated && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        bgcolor: COLORS.primary,
                        color: COLORS.white,
                        fontWeight: 700,
                        fontSize: '10px',
                        px: 0.6,
                        py: 0.1,
                        borderRadius: '4px',
                        mr: 0.5,
                        lineHeight: 1.2,
                        display: 'inline-block',
                        verticalAlign: 'middle'
                      }}
                    >
                      NEW
                    </Typography>
                  )}
                  {task.marked_as_pin && (
                    <PushPin sx={{ fontSize: 12, color: COLORS.primary, flexShrink: 0, mr: 0.5, transform: 'rotate(35deg)' }} />
                  )}
                  {task.parent_ticket_number && (
                    <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: COLORS.gray[400], whiteSpace: 'nowrap', fontSize: '11px', mr: 0.5 }}>
                      {task.parent_ticket_number} ›
                    </Typography>
                  )}
                  {task.ticket_number && (
                    <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: COLORS.text.primary, whiteSpace: 'nowrap', fontSize: '13px', mr: 0.5 }}>
                      {task.ticket_number}
                    </Typography>
                  )}
                  {task.title}
                </OverflowTooltip>
              </Box>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              {!isColumnInBulkMode && (
                <>
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    sx={{ p: 0.5, color: COLORS.gray[500] }}
                  >
                    <MoreVert sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    onClick={(e) => e.stopPropagation()}
                    PaperProps={{
                      sx: {
                        borderRadius: '10px',
                        minWidth: 140,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        mt: 0.5
                      }
                    }}
                  >
                    <MenuItem
                      onClick={(e) => { handleMenuClose(e); handleEditClick(e); }}
                      sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
                    >
                      <Box component="img" src={ICONS.edit} sx={{ width: 14, height: 14 }} />
                      {t('taskViewModal.editTask', 'Edit Task')}
                    </MenuItem>
                    <Divider />

                    <MenuItem
                      onClick={handleTogglePinClick}
                      sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
                    >
                      <PushPin sx={{ fontSize: 14, color: task.marked_as_pin ? COLORS.primary : COLORS.gray[500], transform: 'rotate(35deg)', flexShrink: 0 }} />
                      {task.marked_as_pin
                        ? t('dashboard.unpinTicket', 'Unpin Ticket')
                        : t('dashboard.pinTicket', 'Pin Ticket')}
                    </MenuItem>
                    <Divider />

                    <MenuItem
                      onClick={(e) => { handleMenuClose(e); handleCopyClick(e); }}
                      sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
                    >
                      <Box component="img" src={ICONS.copyLinkTicket} sx={{ width: 14, height: 14 }} />
                      {t('taskViewModal.copyLink')}
                    </MenuItem>
                    <Divider />

                    <MenuItem
                      onClick={(e) => { handleMenuClose(e); handleCopyTitleClick(e); }}
                      sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
                    >
                      <ContentCopy sx={{ fontSize: 14, color: COLORS.gray[500] }} />
                      {t('taskViewModal.copyTitle', 'Copy Title')}
                    </MenuItem>
                    <Divider />

                    {(() => {
                      const hasLinks = task.linked_tickets && Object.keys(task.linked_tickets).length > 0;
                      return (
                        <>
                          <MenuItem
                            disabled={!hasLinks}
                            onClick={(e) => {
                              handleMenuClose(e);
                              setViewingLinksTask(task);
                              setIsViewLinksModalOpen(true);
                            }}
                            sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
                          >
                            <Box component="img" src={ICONS.viewLink} sx={{ width: 14, height: 14, opacity: hasLinks ? 1 : 0.5 }} />
                            {t('links.tooltips.viewLinks')}
                          </MenuItem>
                          <Divider />
                        </>
                      );
                    })()}

                    <MenuItem
                      onClick={(e) => {
                        handleMenuClose(e);
                        setLinkingTask(task);
                        setIsLinkModalOpen(true);
                      }}
                      sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
                    >
                      <Box component="img" src={ICONS.attachFile} sx={{ width: 14, height: 14 }} />
                      {t('links.tooltips.addLink')}
                    </MenuItem>
                    <Divider />

                    {user?.role !== USER_ROLES.USER && (
                      <MenuItem
                        onClick={(e) => { handleMenuClose(e); handleDeleteClick(e); }}
                        sx={{
                          gap: 1,
                          fontSize: '12px',
                          py: 0.4
                        }}
                      >
                        <Box component="img" src={ICONS.delete} sx={{ width: 14, height: 14 }} />
                        {t('dashboard.deleteTask', 'Delete Task')}
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>

          {/* Due date & Labels */}
          <Box display="flex" alignItems="center" gap={1} mb={{ xs: 0.75, lg: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <img src={ICONS.calendarTicket} alt="due date" style={{ width: 12, height: 12 }} />
              <Tooltip title={task.dueDateTooltip || task.dueDate} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '10px',
                    color: COLORS.gray[500],
                    fontWeight: 600
                  }}
                >
                  {task.dueDate}
                </Typography>
              </Tooltip>
            </Box>
            {task.labels && task.labels.length > 0 && (
              <LabelChips labels={task.labels} />
            )}
          </Box>

          {/* Priority & Assignee */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={task.sub_task_count ? 1 : 0}>
            <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: '999px',
                border: `1px solid ${getPriorityColor(task.priority, { priorities: dropdowns.priorities })}`,
                color: getPriorityColor(task.priority, { priorities: dropdowns.priorities }),
                fontSize: '10px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                lineHeight: 1.4
              }}>
                <Flag sx={{ fontSize: 11 }} />
                {getDisplayName(task.priority, 'priority')}
              </Box>
            </Box>
            <AssigneeAvatarStack assignees={task.assignees} assignee={task.assignee} />
          </Box>

          <InlineSubtaskList task={task} />
        </CardContent>
      </Card>
    );
  });

  // TaskListView component moved to separate file



  return (
    <div className="dashboard">
      <Navbar />

      {audioPromptReady && showAudioPrompt && (
        <Box className="audio-permission-banner">
          <img src={bellIcon} alt="" className="audio-permission-banner-icon" />
          <Typography className="audio-permission-banner-text">
            {t('dashboard.enableNotificationSounds', 'Click anywhere to enable notification sounds')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowAudioPrompt(false)}
            className="audio-permission-banner-close"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      <main className="main">
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1, borderRadius: '8px' }}>
          <Typography
            variant="h6"
            fontWeight={600}
            color={COLORS.text.muted}
          >
            {t('dashboard.title')}
          </Typography>
          <Box display="flex" gap={SIZING.gap.xs} alignItems="center">
            {savedFilters.header}
            <Tooltip title={t('dashboard.refresh')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
              <IconButton
                onClick={handleRefresh}
                disabled={selectedProject === FILTER_VALUES.ALL || isRefreshing}
                className="dashboard-header-icon-button"
                aria-label={t('dashboard.refresh')}
              >
                <img
                  src={refreshIcon}
                  alt={t('dashboard.refresh')}
                  className={`dashboard-header-icon dashboard-header-icon-refresh ${isRefreshing ? 'is-spinning' : ''}`}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('dashboard.notifications')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
              <IconButton
                onClick={handleNotificationsClick}
                className={`dashboard-header-icon-button ${bellRinging ? 'bell-ringing' : ''}`}
                aria-label={t('dashboard.notifications')}
              >
                <Badge
                  badgeContent={notificationBadgeLabel}
                  invisible={!notificationBadgeLabel}
                  className="dashboard-notification-badge"
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <img
                    src={bellIcon}
                    alt={t('dashboard.notifications')}
                    className="dashboard-header-icon"
                  />
                </Badge>
              </IconButton>
            </Tooltip>
            <HeaderProfileMenu />
          </Box>
        </Box>






        {/* Filters */}
        <FilterHeader
          savedFilterRevision={savedFilterRevision}
          savedFilterActions={savedFilters.actions}
          searchTerm={searchTerm}
          selectedProject={selectedProject}
          selectedSprint={selectedSprint}
          selectedTaskType={selectedTaskType}
          selectedLabels={selectedLabels}
          selectedPriority={selectedPriority}
          selectedAssignee={selectedAssignee}
          selectedCreatedBy={selectedCreatedBy}
          selectedStatus={selectedStatus}
          selectedEpic={selectedEpic}
          startDate={startDate}
          dueDate={dueDate}
          selectedSort={selectedSort}
          isScrumProject={isSelectedProjectScrum}
          onSearchChange={(value) => {
            if (selectedProject !== FILTER_VALUES.ALL && dropdownsFetched.current) {
              if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
              }
              if (value === '') {
                searchTermRef.current = '';
                setSearchTerm('');
                dispatch(setFilters({ searchTerm: '' }));
                dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters({ search: '' }), page: 1, append: false }));
              } else {
                searchTermRef.current = value;
                setSearchTerm(value);
                dispatch(setFilters({ searchTerm: value }));
                dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters({ search: value }), page: 1, append: false }));
              }
            }
          }}
          onProjectChange={(e) => {
            const value = e.target.value;
            const targetProject = projectDropdownsByUser?.find(project => String(project.id) === String(value));
            const nextSprintSelection = (targetProject?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase()
              ? [SCRUM_BACKLOG_SENTINEL]
              : [FILTER_VALUES.ALL];
            const nextFilters = {
              searchTerm: '',
              selectedProject: value,
              selectedPriority: FILTER_VALUES.ALL,
              selectedAssignee: FILTER_VALUES.ALL,
              selectedCreatedBy: FILTER_VALUES.ALL,
              selectedTaskType: FILTER_VALUES.ALL,
              selectedStatus: FILTER_VALUES.ALL,
              selectedLabels: [FILTER_VALUES.ALL],
              selectedSprint: nextSprintSelection,
              selectedEpic: FILTER_VALUES.ALL,
              startDate: '',
              dueDate: '',
              selectedSort: 'NEWEST_FIRST',
              epicStartDate: null,
              epicDueDate: null
            };
            setSelectedProject(value);
            searchTermRef.current = '';
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
              searchTimeoutRef.current = null;
            }
            setSearchTerm('');
            if (searchInputRef.current) searchInputRef.current.value = '';
            setSelectedPriority(FILTER_VALUES.ALL);
            setSelectedAssignee(FILTER_VALUES.ALL);
            setSelectedCreatedBy(FILTER_VALUES.ALL);
            setSelectedTaskType(FILTER_VALUES.ALL);
            setSelectedStatus(FILTER_VALUES.ALL);
            setSelectedLabels([FILTER_VALUES.ALL]);
            setSelectedSprint(nextSprintSelection);
            setSelectedEpic(FILTER_VALUES.ALL);
            setStartDate('');
            setDueDate('');
            setSelectedSort('NEWEST_FIRST');
            dispatch(setFilters(nextFilters));
            if (value !== FILTER_VALUES.ALL) {
              dispatch(saveUserPreference({
                preferenceKey: 'last_selected_project',
                preferenceValue: { project_id: value }
              }));
            }
            if (value !== FILTER_VALUES.ALL) {
              dispatch(fetchTicketDropdowns(value));
            }
          }}
          onSprintChange={(e) => {
            const rawValue = e.target.value;
            let newValue = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
            newValue = Array.isArray(newValue) ? newValue : [newValue];
            newValue = newValue.filter(v => v !== '' && v != null);
            if (newValue.length === 0 || newValue.includes(FILTER_VALUES.ALL)) {
              newValue = [FILTER_VALUES.ALL];
            }
            setSelectedSprint(newValue);
            dispatch(setFilters({ selectedSprint: newValue }));

            // Fetch here as well as updating the controlled filter state so a
            // sprint selection is applied immediately, including when the
            // general filter effect is skipped during a view transition.
            if (
              selectedProject !== FILTER_VALUES.ALL &&
              !isInitialLoad.current &&
              dropdownsFetched.current &&
              viewMode !== VIEW_MODES.GANTT &&
              viewMode !== VIEW_MODES.CALENDAR
            ) {
              const nextFilters = getCurrentFilters({ sprint: newValue });
              const fetchKey = JSON.stringify({ projectId: selectedProject, filters: nextFilters, page: 1, append: false });
              if (lastFetchKeyRef.current !== fetchKey) {
                lastFetchKeyRef.current = fetchKey;
                filtersChanged.current = true;
                dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: nextFilters, page: 1, append: false }));
              }
            }
          }}
          onTaskTypeChange={(e) => {
            const newValue = e.target.value;
            setSelectedTaskType(newValue);
            dispatch(setFilters({ selectedTaskType: newValue }));
          }}
          onLabelsChange={(e) => {
            let newValue = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
            newValue = newValue.filter(v => v !== '' && v != null);
            if (newValue.includes(FILTER_VALUES.ALL)) {
              if (selectedLabels.includes(FILTER_VALUES.ALL)) {
                newValue = newValue.filter(v => v !== FILTER_VALUES.ALL);
              } else {
                newValue = [FILTER_VALUES.ALL];
              }
            } else if (newValue.length === 0) {
              newValue = [FILTER_VALUES.ALL];
            }
            setSelectedLabels(newValue);
            dispatch(setFilters({ selectedLabels: newValue }));
          }}
          onPriorityChange={(e) => {
            const newValue = e.target.value;
            setSelectedPriority(newValue);
            dispatch(setFilters({ selectedPriority: newValue }));
          }}
          onAssigneeChange={(e) => {
            const newValue = e.target.value;
            setSelectedAssignee(newValue);
            dispatch(setFilters({ selectedAssignee: newValue }));
          }}
          onCreatedByChange={(e) => {
            const newValue = e.target.value;
            setSelectedCreatedBy(newValue);
            dispatch(setFilters({ selectedCreatedBy: newValue }));
          }}
          onStatusChange={(e) => {
            const newValue = e.target.value;
            setSelectedStatus(newValue);
            dispatch(setFilters({ selectedStatus: newValue }));
          }}
          onEpicChange={(newValue) => {
            setSelectedEpic(newValue);
            dispatch(setFilters({ selectedEpic: newValue }));
          }}
          onStartDateChange={(e) => {
            const value = e.target.value;
            setStartDate(value);
            dispatch(setFilters({ startDate: value }));
          }}
          onDueDateChange={(e) => {
            const value = e.target.value;
            setDueDate(value);
            dispatch(setFilters({ dueDate: value }));
          }}
          onSortChange={(e) => {
            const newValue = e.target.value;
            setSelectedSort(newValue);
            dispatch(setFilters({ selectedSort: newValue }));
          }}
          projectDropdownsByUser={projectDropdownsByUser}
          dropdowns={dropdowns}
          dropdownsLoading={dropdownsLoading}
          onCreatePriority={handleCreateFilterPriority}
          onUpdatePriority={handleUpdateFilterPriority}
          onDeletePriority={handleDeleteFilterPriority}
          onCreateTaskType={handleCreateFilterTaskType}
          onUpdateTaskType={handleUpdateFilterTaskType}
          onDeleteTaskType={handleDeleteFilterTaskType}
          onCreateLabel={handleCreateFilterLabel}
          onUpdateLabel={handleUpdateFilterLabel}
          onDeleteLabel={handleDeleteFilterLabel}
          onCreateSprint={handleCreateFilterSprint}
          onUpdateSprint={handleUpdateFilterSprint}
          onDeleteSprint={handleDeleteFilterSprint}
          searchEpics={searchEpicsCallback}
          viewMode={viewMode}
          canManageTicketAttributes={canManageTicketAttributes}
          onClearFilters={handleClearFilters}
          getPriorityColor={getPriorityColor}
          getStatusColor={getStatusColor}
        />

        {/* <Divider sx={{ my: 1.5 }} /> */}

        {/* Sprint Header — class used by fullscreen layout (avoid brittle nth-child when main structure changes) */}
        <Box className="dashboard-view-toolbar" display="flex" justifyContent="space-between" alignItems="center" mb={SIZING.margin.sm} sx={{ boxShadow: `0 0 0 0.0125rem ${COLORS.brand.primaryDark}`, borderRadius: SIZING.radius.sm, px: SIZING.padding.md, py: '0.25rem' }}>
          <Box display="flex" alignItems="center" gap={SIZING.gap.md} sx={{ minWidth: 0, maxWidth: '60%' }}>
            <img src={ICONS.myCurrentSprint} alt="" style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }} />
            {selectedProjectDetails ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: SIZING.gap.xs, minWidth: 0 }}>
                <Tooltip
                  title={selectedProjectDetails.name || ''}
                  arrow
                  placement="bottom-start"
                  disableHoverListener={!selectedProjectDetails.name || selectedProjectDetails.name.length < 25}
                  slotProps={{ tooltip: { className: 'app-tooltip' } }}
                >
                  <Typography
                    variant="h5"
                    noWrap
                    sx={{
                      fontSize: '18px',
                      color: COLORS.gray[900],
                      fontWeight: 600,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flexShrink: projectFlexShrink
                    }}
                  >
                    {selectedProjectDetails.name || t('dashboard.project')}
                  </Typography>
                </Tooltip>
                {selectedProjectDetails.space_name && (
                  <>
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: '18px',
                        color: COLORS.gray[900],
                        fontWeight: 600,
                        flexShrink: 0,
                        px: 0.5
                      }}
                    >
                      -
                    </Typography>
                    <Tooltip
                      title={selectedProjectDetails.space_name || ''}
                      arrow
                      placement="bottom-start"
                      disableHoverListener={!selectedProjectDetails.space_name || selectedProjectDetails.space_name.length < 25}
                      slotProps={{ tooltip: { className: 'app-tooltip' } }}
                    >
                      <Typography
                        variant="h5"
                        noWrap
                        sx={{
                          fontSize: '18px',
                          color: COLORS.gray[500],
                          fontWeight: 600,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flexShrink: spaceFlexShrink
                        }}
                      >
                        {selectedProjectDetails.space_name}
                      </Typography>
                    </Tooltip>
                  </>
                )}
                {selectedProjectStatusConfig && (
                  <Box className={`dashboard-project-status dashboard-project-status--${selectedProjectStatusConfig.colorClass}`}>
                    <SelectedProjectStatusIcon className="dashboard-project-status__icon" />
                    <Typography
                      component="span"
                      className="dashboard-project-status__text"
                    >
                      {selectedProjectDetails.status}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  fontSize: '18px',
                  color: COLORS.gray[900]
                }}
              >
                {sprintHeaderText}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenAddTaskModal()}
              disabled={!projectDropdownsByUser || projectDropdownsByUser.length === 0}
              sx={{
                bgcolor: COLORS.brand.accent,
                '&:hover': { bgcolor: COLORS.brand.accentHover },
                borderRadius: SIZING.radius.sm,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {t('dashboard.createTask')}
            </Button>
            <Button
              className="view-btn"
              startIcon={<Add />}
              endIcon={<KeyboardArrowDown />}
              onClick={(e) => setViewMenuAnchor(e.currentTarget)}
            >
              {viewMode === VIEW_MODES.GANTT ? t('dashboard.gantt') :
               viewMode === VIEW_MODES.CALENDAR ? t('dashboard.calendar') :
               viewMode === VIEW_MODES.LIST ? t('dashboard.list') :
               t('dashboard.board')}
            </Button>
            <Tooltip
              title={isFullscreen ? t('dashboard.exitFullscreen') : t('dashboard.enterFullscreen')}
              arrow
              slotProps={{
                tooltip: {
                  className: 'app-tooltip'
                }
              }}
            >
              <IconButton
                onClick={handleToggleFullscreen}
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#e0e0e0'
                  }
                }}
              >
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={viewMenuAnchor}
              open={Boolean(viewMenuAnchor)}
              onClose={() => setViewMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: viewMenuAnchor?.offsetWidth || 'auto'
                  }
                }
              }}
            >
              <MenuItem
                className={`view-menu-item ${showGanttView ? 'view-menu-item-selected' : ''}`}
                onClick={() => {
                  handleSelectViewMode(VIEW_MODES.GANTT);
                  setViewMenuAnchor(null);
                }}
              >
                <Timeline className="view-menu-icon" />
                {t('dashboard.gantt')}
              </MenuItem>
              <Divider className="view-menu-divider" />
              <MenuItem
                className={`view-menu-item ${showCalendarView ? 'view-menu-item-selected' : ''}`}
                onClick={() => {
                  handleSelectViewMode(VIEW_MODES.CALENDAR);
                  setViewMenuAnchor(null);
                }}
              >
                <CalendarMonth className="view-menu-icon" />
                {t('dashboard.calendar')}
              </MenuItem>
              <Divider className="view-menu-divider" />
              <MenuItem
                className={`view-menu-item ${showBoardView ? 'view-menu-item-selected' : ''}`}
                onClick={() => {
                  handleSelectViewMode(VIEW_MODES.BOARD);
                  setViewMenuAnchor(null);
                }}
              >
                <ViewKanban className="view-menu-icon" />
                {t('dashboard.board')}
              </MenuItem>
              <Divider className="view-menu-divider" />
              <MenuItem
                className={`view-menu-item ${showListView ? 'view-menu-item-selected' : ''}`}
                onClick={() => {
                  handleSelectViewMode(VIEW_MODES.LIST);
                  setViewMenuAnchor(null);
                }}
              >
                <FormatListBulleted className="view-menu-icon" />
                {t('dashboard.list')}
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Bulk Actions are now handled within the column kebab menu */}

        {/* Content Area */}
        <Box
          className={`content-area ${viewMode === VIEW_MODES.CALENDAR ? 'calendar-view-container' : ''}`}
          onDragOver={onContentAreaDragOver}
          sx={{
            height: 'calc(100vh - 200px)',
            minHeight: '43rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            position: 'relative',
            zIndex: 1
          }}
        >
          {!initialLoadDone || dropdownsLoading || projectDropdownsByUserLoading || ticketsLoading ? (
            viewMode === VIEW_MODES.LIST ? (
              <ListViewSkeleton />
            ) : viewMode === VIEW_MODES.BOARD ? (
              <BoardViewSkeleton />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <Typography>{t('dashboard.loading')}</Typography>
              </Box>
            )
          ) : projectDropdownsByUser.length === 0 ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="200px" gap={2}>
              <Typography variant="h6" color="text.secondary">{t('dashboard.noProjectsAssigned')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('dashboard.contactAdminForAccess')}</Typography>
            </Box>
          ) : taskColumns.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Typography>{t('dashboard.noStatusColumns')}</Typography>
            </Box>
          ) :
            viewMode === VIEW_MODES.BOARD ? (
              <Box
                ref={boardScrollRef}
                onDragOver={onBoardDragOver}
                className="tickets-container-board"
              >
                <Box
                  display="flex"
                  gap={2}
                  className={draggedStatus ? 'board-drag-overlay' : ''}
                  sx={{
                    minWidth: 'max-content',
                    height: '100%',
                    position: 'relative',
                    px: 2,
                    py: 1
                  }}
                >
                  {taskColumns.map((column) => {
                    const rawThemeColor = column.colour ? column.colour + '29' : getColumnTheme(column.title);
                    const baseHex = rawThemeColor.slice(0, 7);
                    const scrollbarThumbColor = baseHex + '60';
                    const scrollbarThumbHover = baseHex + '90';

                    return (
                      <Box
                        key={column.id}
                        data-column-id={column.id}
                        onDragOver={(e) => {
                          if (draggedStatus && canReorderStatus) {
                            handleColumnDragOver(e, column.id);
                          } else {
                            handleDragOver(e, column.id);
                          }
                        }}
                        onDragLeave={(e) => {
                          if (draggedStatus && canReorderStatus) {
                            handleColumnDragLeave(e, column.id);
                          } else {
                            handleDragLeave(e);
                          }
                        }}
                        onDrop={(e) => {
                          if (draggedStatus && canReorderStatus) {
                            handleColumnDrop(e, column.id);
                          } else {
                            handleDrop(e, column.id);
                          }
                        }}
                        className={`drag-drop-column ${draggedStatus?.id === column.id ? 'dragged' : ''} ${bulkModeColumnId === column.id ? 'bulk-mode-column' : ''}`}
                        sx={{
                          backgroundColor: rawThemeColor,
                          borderRadius: '12px',
                          padding: '8px',
                          border: `0.5px solid ${getStatusColor(column.id)}20`,
                        }}
                      >
                        {/* Column Header */}
                        <Box
                          draggable={canReorderStatus}
                          onDragStart={canReorderStatus ? (e) => handleStatusDragStart(e, column) : undefined}
                          onDragEnd={canReorderStatus ? handleStatusDragEnd : undefined}
                          className={`column-header ${canReorderStatus ? 'status-header-draggable' : 'status-header-disabled'
                            } ${draggedStatus?.id === column.id ? 'status-ghost-placeholder' : ''
                            } ${bulkModeColumnId === column.id ? 'bulk-mode-header' : ''
                            }`}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          mb={0}
                          px={{ xs: 1.25, lg: 1.5 }}
                          py={{ xs: 1, lg: 1.5 }}
                          sx={{ 
                            position: 'relative',
                            bgcolor: 'white',
                            border: '0.5px solid rgba(8, 43, 114, 0.2)',
                            borderRadius: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                          }}
                        >
                          {/* Left actions: Drag and Bulk Select */}
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ position: 'absolute', left: 8 }}>
                            {bulkModeColumnId === column.id && (
                              <>
                                <Tooltip title={selectedTasksByColumn[bulkModeColumnId]?.size === tasksByStatusMap.get(bulkModeColumnId)?.length ? t('dashboard.bulkActions.deselectAll') : (isSelectionLimitReached ? t('dashboard.bulkActions.selectionLimitReached') : t('dashboard.bulkActions.selectAll'))} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={selectedTasksByColumn[bulkModeColumnId]?.size === tasksByStatusMap.get(bulkModeColumnId)?.length ? handleDeselectAllInColumn : handleSelectAllInColumn}
                                      className="bulk-select-all-btn"
                                      aria-label={selectedTasksByColumn[bulkModeColumnId]?.size === tasksByStatusMap.get(bulkModeColumnId)?.length ? t('dashboard.bulkActions.deselectAllInColumn') : t('dashboard.bulkActions.selectAllInColumn')}
                                      role="checkbox"
                                      aria-checked={selectedTasksByColumn[bulkModeColumnId]?.size === tasksByStatusMap.get(bulkModeColumnId)?.length}
                                      disabled={isSelectionLimitReached && selectedTasksByColumn[bulkModeColumnId]?.size !== tasksByStatusMap.get(bulkModeColumnId)?.length}
                                    >
                                      {selectedTasksByColumn[bulkModeColumnId]?.size === tasksByStatusMap.get(bulkModeColumnId)?.length ? (
                                        <CheckBox className="bulk-checkbox-selected" />
                                      ) : (
                                        <CheckBoxOutlineBlank className="bulk-checkbox-unselected" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('dashboard.bulkActions.selectAllInfo')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                  <InfoOutlined className="bulk-select-all-info-icon" onClick={(e) => e.stopPropagation()} />
                                </Tooltip>
                              </>
                            )}
                          </Box>

                          {/* Centered Title, Icon and Count */}
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ px: bulkModeColumnId === column.id ? 7 : 4, minWidth: 0, width: '100%' }}>
                            {getStatusIcon(column.title) && (
                              <img 
                                src={getStatusIcon(column.title)} 
                                alt="" 
                                style={{ width: 18, height: 18, flexShrink: 0 }} 
                              />
                            )}
                            <OverflowTooltip
                              title={column.title}
                              tooltipWidth={300}
                              variant="subtitle1"
                              fontWeight="600"
                              sx={{
                                fontSize: '14px',
                                color: getStatusColor(column.id),
                              }}
                              placement="bottom-start"
                            />
                            <Chip
                              label={column.count}
                              size="small"
                              sx={{
                                bgcolor: COLORS.gray[100],
                                color: COLORS.gray[600],
                                fontSize: '11px',
                                fontWeight: 600,
                                height: 20,
                                minWidth: 24,
                                flexShrink: 0
                              }}
                            />
                          </Box>

                          {/* Right Menu */}
                          <IconButton
                            size="small"
                            onClick={(e) => kebabMenu.handleOpen(e, column.id)}
                            data-menu-id={column.id}
                            sx={{ p: 0.5, position: 'absolute', right: 8 }}
                            aria-label="More actions"
                            aria-haspopup="menu"
                          >
                            <MoreVert sx={{ fontSize: 16, color: COLORS.gray[500] }} />
                          </IconButton>
                        </Box>

                        {/* Static Add Task Button */}
                        <Button
                          fullWidth
                          variant="text"
                          startIcon={<Add sx={{ fontSize: '18px !important' }} />}
                          onClick={() => handleOpenAddTaskModal(column.id)}
                          sx={{
                            mb: 1,
                            mt: 0.5,
                            color: COLORS.slate[500],
                            justifyContent: 'center',
                            textTransform: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            py: 0.8,
                            px: 2,
                            borderRadius: 2,
                            border: 'none',
                            bgcolor: COLORS.slate[50],
                            '&:hover': {
                              bgcolor: COLORS.slate[100],
                              color: COLORS.gray[600]
                            },
                            '@media (max-width: 1380px)': {
                              py: 0.5,
                              px: 1.5,
                              fontSize: '13px'
                            }
                          }}
                        >
                          {t('dashboard.addTask')}
                        </Button>

                        {/* Scrollable Column Content */}
                        <Box
                          ref={(el) => setColumnRef(el, column.id)}
                          className="scrollable-column-content"
                          sx={{
                            minHeight: 0,
                            pb: 0,
                            pt: '4px',
                            flex: 1,
                            px: 0,
                            scrollbarWidth: 'thin',
                            scrollbarColor: `${scrollbarThumbColor} transparent`,
                            '&::-webkit-scrollbar': {
                              width: '6px',
                              height: '6px'
                            },
                            '&::-webkit-scrollbar-track': {
                              background: 'transparent'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: scrollbarThumbColor,
                              borderRadius: '10px'
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                              backgroundColor: scrollbarThumbHover
                            }
                          }}
                        >

                          {tasks
                            .filter(task => task.status === column.id)
                            .map((task, index, filteredTasks) => (
                              <Box key={task.id} sx={{ mb: index === filteredTasks.length - 1 ? 4 : 1.5 }}>
                                <TaskCard
                                  task={task}
                                  onDragStart={bulkModeColumnId !== column.id ? stableHandleDragStart : undefined}
                                  onDragEnd={bulkModeColumnId !== column.id ? stableHandleDragEnd : undefined}
                                  isDragging={bulkModeColumnId !== column.id && draggedTask?.id === task.id}
                                  columnId={column.id}
                                  isNewlyCreated={newlyCreatedTaskId === task.id}
                                />
                              </Box>
                            ))}

                          {/* Loading indicator */}
                          {columnPagination?.[column.id]?.loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <Typography variant="caption" sx={{ color: COLORS.gray[500] }}>
                                {t('dashboard.loadingMore')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )
                  })}

                  {/* Add Column Button */}
                  {canManageStatus && (
                    <Box
                      component="button"
                      type="button"
                      sx={{
                        minWidth: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        pt: 8,
                        cursor: 'pointer',
                        borderRadius: 3,
                        border: `2px dashed ${COLORS.slate[300]}`,
                        bgcolor: COLORS.slate[50],
                        transition: 'all 0.2s ease-in-out',
                        borderStyle: 'dashed',
                        outline: 'none',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        '&:hover': {
                          borderColor: COLORS.slate[400],
                          bgcolor: COLORS.slate[100]
                        },
                        '&:hover .add-icon': {
                          color: COLORS.gray[600],
                          transform: 'scale(1.1)'
                        },
                        '&:hover .add-text': {
                          color: COLORS.gray[600]
                        }
                      }}
                      onClick={() => setIsAddColumnModalOpen(true)}
                    >
                      <AddCircleOutline
                        className="add-icon"
                        sx={{
                          fontSize: 32,
                          color: COLORS.slate[400],
                          mb: 1,
                          transition: 'all 0.2s ease-in-out'
                        }}
                      />
                      <Typography
                        className="add-text"
                        variant="body2"
                        sx={{
                          fontSize: '13px',
                          textAlign: 'center',
                          fontWeight: 500,
                          color: COLORS.slate[500],
                          transition: 'color 0.2s ease-in-out'
                        }}
                      >
                        {t('dashboard.addColumn')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : viewMode === VIEW_MODES.GANTT ? (
              <GanttView
                projectId={selectedProject}
                filters={(() => {
                  // Create Gantt-specific filters - get filters from Redux store directly
                  const currentFilters = getCurrentFilters();
                  // Add epic date filters from Redux store
                  const ganttFilters = {
                    ...currentFilters,
                    epicStartDate: filters.epicStartDate,
                    epicDueDate: filters.epicDueDate,
                    startDate: startDate || undefined,
                    dueDate: dueDate || undefined
                  };
                  // Only remove these if they exist to avoid undefined values
                  if ('start_date' in ganttFilters) delete ganttFilters.start_date;
                  if ('due_date' in ganttFilters) delete ganttFilters.due_date;
                  // Keep startDate, dueDate, epicStartDate, and epicDueDate for Gantt processing
                  return ganttFilters;
                })()}
                onTaskClick={handleViewTask}
                onCreateTask={(dateString) => {
                  handleOpenAddTaskModal(null, true, dateString);
                }}
              />
            ) : viewMode === VIEW_MODES.LIST ? (
              <TaskListView
                tasks={tasks}
                newlyCreatedTaskId={newlyCreatedTaskId}
                loadingTaskId={loadingTaskId}
                selectedProject={selectedProject}
                searchTerm={searchTerm}
                selectedPriority={selectedPriority}
                selectedStatus={selectedStatus}
                selectedAssignee={selectedAssignee}
                selectedTaskType={selectedTaskType}
                dispatch={dispatch}
                fetchTicketsByProject={fetchTicketsByProject}
                handleViewTask={handleViewTask}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                getStatusLabel={getStatusLabel}
                getStatusColor={getStatusColor}
                getPriorityColor={getPriorityColor}
                onEditColumn={handleEditColumn}
                onDeleteColumn={handleDeleteColumn}
                getDisplayName={getDisplayName}
                FILTER_VALUES={FILTER_VALUES}
                loadMoreColumnData={loadMoreColumnData}
                draggedTask={draggedTask}
                dragOverColumn={dragOverColumn}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                draggedStatus={draggedStatus}
                handleStatusDragStart={handleStatusDragStart}
                handleStatusDragEnd={handleStatusDragEnd}
                performStatusReorder={performStatusReorder}
                selectedTasksByColumn={selectedTasksByColumn}
                selectedTaskSets={selectedTaskSets}
                bulkModeColumnId={bulkModeColumnId}
                isBulkMode={isBulkMode}
                isSelectionLimitReached={isSelectionLimitReached}
                onTaskSelect={handleTaskSelect}
                onBulkEdit={handleBulkEdit}
                onBulkDelete={handleBulkDelete}
                onEnterBulkMode={enterBulkMode}
                onExitBulkMode={exitBulkMode}
                onSelectAllInColumn={handleSelectAllInColumn}
                onDeselectAllInColumn={handleDeselectAllInColumn}
                selectAllModeByColumn={selectAllModeByColumn}
                tasksByStatusMap={tasksByStatusMap}
                onViewLinks={handleViewLinks}
                onAddLink={handleAddLink}
                onRefreshTickets={refreshCurrentTickets}
                t={t}
              />
            ) : (
              <CalendarView
                projectId={selectedProject}
                filters={getCurrentFilters()}
                onTaskClick={handleViewTask}
                onCreateTask={(dateString) => {
                  handleOpenAddTaskModal(null, true, dateString);
                }}
              />
            )
          }
        </Box>





      </main>

      {/* Kebab Menu */}
      <KebabMenu
        anchorEl={kebabMenu.anchorEl}
        open={kebabMenu.isOpen}
        onClose={kebabMenu.handleClose}
        menuItems={getKebabMenuItems}
        onItemClick={handleKebabMenuItemClick}
        disableClickAway={isBulkMode}
        scrollWithAnchor
      />

      <AddTaskModal
        open={isAddTaskModalOpen}
        onClose={() => {
          handleOpenAddTaskModal(null, false, null);
        }}
        onSubmit={handleAddTask}
        selectedProjectId={selectedProject !== FILTER_VALUES.ALL ? selectedProject : null}
        preselectedStatusId={preselectedStatusId}
        selectedDate={selectedDateForTask}
        userRole={user?.role}
      />

      <EditTaskModal
        open={isEditTaskModalOpen}
        onClose={() => setIsEditTaskModalOpen(false)}
        onSubmit={handleUpdateTask}
        taskData={selectedTask}
        selectedProjectId={selectedProject !== FILTER_VALUES.ALL ? selectedProject : null}
        userRole={user?.role}
      />

      <CommonDeleteConfirmDialog
        open={isDeleteTaskModalOpen}
        onClose={() => setIsDeleteTaskModalOpen(false)}
        onConfirm={() => handleDeleteTask(selectedTask)}
        title={t('taskModal.deleteTask')}
        question={t('taskModal.confirmDeleteTask')}
        itemName={selectedTask?.title}
        warning={t('taskModal.actionCannotBeUndone')}
        confirmText={t('taskModal.deleteTask')}
        cancelText={t('common.cancel')}
      />

      <TaskViewModal
        open={isTaskViewModalOpen}
        onClose={() => {
          setIsTaskViewModalOpen(false);
          setTaskUpdateHandler(null);
          setTaskViewInitialEditMode(false);
        }}
        onEdit={handleEdit}
        taskData={selectedTask}
        onViewTask={(task) => { setSelectedTask(task); setIsTaskViewModalOpen(true); }}
        dashboardTasks={tasks}
        onTaskUpdate={taskUpdateHandler}
        initialEditMode={taskViewInitialEditMode}
      />
      <Dialog
        open={isMoveConfirmOpen}
        onClose={handleCancelMoveTask}
        PaperProps={{ className: 'task-move-confirm-dialog' }}
      >
        <DialogTitle className="task-move-confirm-title">
          <Box className="task-move-confirm-title-row">
            <Avatar className="task-move-confirm-icon">
              <SwapHoriz />
            </Avatar>
            <Box className="task-move-confirm-text">
              <Typography variant="h6" className="task-move-confirm-heading">
                {t('dashboard.moveTicket')}
              </Typography>
              <Typography variant="body2" className="task-move-confirm-subtitle">
                {t('dashboard.confirmMoveSubtitle')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className="task-move-confirm-content">
          <Typography variant="body1" className="task-move-confirm-message">
            {t('dashboard.confirmMoveMessage', {
              task: pendingMove?.task
                ? `${pendingMove.task.ticket_number || pendingMove.task.code || ''}${pendingMove.task.ticket_number || pendingMove.task.code ? ': ' : ''}${pendingMove.task.title || ''}`
                : '',
              from: dropdowns.statuses.find(s => s.id === pendingMove?.task?.status)?.name || t('dashboard.unknown'),
              to: taskColumns.find(col => col.id === pendingMove?.columnId)?.title || ''
            })}
          </Typography>
        </DialogContent>
        <DialogActions className="task-move-confirm-actions">
          <Button
            onClick={handleCancelMoveTask}
            disabled={isMoveConfirmLoading}
            className="task-move-confirm-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmMoveTask}
            variant="contained"
            disableElevation
            disabled={isMoveConfirmLoading}
            className="task-move-confirm-submit"
          >
            {t('dashboard.move')}
          </Button>
        </DialogActions>
      </Dialog>
      <AddBoardColumnModal
        open={isAddColumnModalOpen && canManageStatus}
        onClose={() => setIsAddColumnModalOpen(false)}
        onSubmit={(data) => { handleAddColumn(data); setIsAddColumnModalOpen(false); }}
        selectedProjectId={selectedProject !== FILTER_VALUES.ALL ? selectedProject : null}
      />

      <EditBoardColumnModal
        open={isEditColumnModalOpen && canManageStatus}
        onClose={() => setIsEditColumnModalOpen(false)}
        onSubmit={handleUpdateColumn}
        columnData={selectedColumn}
      />

      <DeleteBoardColumnModal
        open={isDeleteColumnModalOpen && canManageStatus}
        onClose={() => setIsDeleteColumnModalOpen(false)}
        onConfirm={handleConfirmDeleteColumn}
        columnData={selectedColumn}
      />

      <BulkEditModal
        open={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        onSubmit={handleBulkEditSubmit}
        selectedTasks={bulkEditSelectedTasks}
        loading={bulkEditLoading}
        bulkModeColumnId={bulkModeColumnId}
        totalCount={tickets[dropdowns.statuses?.find(s => s.id === bulkEditColumnId)?.name]?.total ?? 0}
      />

      <BulkDeleteModal
        open={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        selectedTasks={bulkDeleteSelectedTasks}
        loading={bulkDeleteLoading}
        totalCount={tickets[dropdowns.statuses?.find(s => s.id === bulkDeleteColumnId)?.name]?.total ?? 0}
      />

      <LinkTicketModal
        open={isLinkModalOpen}
        onClose={() => {
          setIsLinkModalOpen(false);
          setLinkingTask(null);
        }}
        primaryTicket={{
          number: linkingTask?.code,
          title: linkingTask?.title
        }}
        relationshipTypes={memoizedRelationshipTypes}
        currentTicketId={linkingTask?.id}
        existingLinkedTickets={(() => {
          try {
            return linkingTask?.linked_tickets ?
              Object.entries(linkingTask.linked_tickets).flatMap(([relationshipType, relationshipData]) =>
                (relationshipData?.tickets || []).map(ticket => ({
                  ...ticket,
                  relationshipType
                })) || []
              ) : [];
          } catch (error) {
            console.error('Error processing linked tickets:', error);
            return [];
          }
        })()}
        searchTickets={searchTicketsCallback}
        onLinkTickets={async (linkData) => {
          try {
            if (!selectedProject || selectedProject === FILTER_VALUES.ALL) {
              toast.error(t('links.errors.selectProject'));
              return;
            }

            await dispatch(linkTickets({
              projectId: selectedProject,
              parentTicketId: linkingTask.id,
              relationshipType: linkData.relationshipType,
              targetTicketIds: linkData.targetTickets.map(t => t.id).filter(id => id !== linkingTask.id)
            })).unwrap();

            // Modal will close automatically, then refresh data
            requestAnimationFrame(() => {
              dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
            });

            setLinkingTask(null);
          } catch (error) {
            console.error('Failed to link tickets:', error);
          }
        }}
      />

      <ViewLinkedTicketsModal
        open={isViewLinksModalOpen}
        onClose={() => {
          setIsViewLinksModalOpen(false);
          setViewingLinksTask(null);
        }}
        task={viewingLinksTask}
        linkedTickets={viewingLinksTask?.linked_tickets || {}}
        onEditLink={(link) => {
          setEditingLink(link);
          setIsEditLinkModalOpen(true);
          setIsViewLinksModalOpen(false);
        }}
        onDeleteLink={async (link) => {
          try {
            // Extract relationship ID from the new API structure
            const relationshipId = link.ticket.relationship_id;

            await dispatch(deleteLinkTickets(relationshipId)).unwrap();

            // Refresh the task data to update linked tickets
            if (selectedProject !== FILTER_VALUES.ALL) {
              await dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
            }

            // Check current linked tickets before update
            const currentLinkedTickets = viewingLinksTask?.linked_tickets || {};
            const relationshipType = link.relationshipType;
            const currentRelationshipTickets = currentLinkedTickets[relationshipType]?.tickets || [];

            // Update the viewing task's linked tickets by removing the deleted link
            setViewingLinksTask(prevTask => {
              if (!prevTask?.linked_tickets) return prevTask;

              const updatedLinkedTickets = { ...prevTask.linked_tickets };

              if (updatedLinkedTickets[relationshipType]?.tickets) {
                // Create a deep copy of the relationship data to avoid read-only errors
                const relationshipData = { ...updatedLinkedTickets[relationshipType] };
                relationshipData.tickets = relationshipData.tickets.filter(
                  ticket => ticket.id !== link.ticket.id
                );

                // Remove the relationship type if no tickets left
                if (relationshipData.tickets.length === 0) {
                  delete updatedLinkedTickets[relationshipType];
                } else {
                  updatedLinkedTickets[relationshipType] = relationshipData;
                }
              }

              return {
                ...prevTask,
                linked_tickets: updatedLinkedTickets
              };
            });

            // Close modal if this was the last ticket in the last relationship type
            const totalRelationshipTypes = Object.keys(currentLinkedTickets).length;
            const isLastTicketInRelationship = currentRelationshipTickets.length === 1;
            const isLastRelationshipType = totalRelationshipTypes === 1;

            if (isLastTicketInRelationship && isLastRelationshipType) {
              setIsViewLinksModalOpen(false);
              setViewingLinksTask(null);
            }

          } catch (error) {
            console.error('Failed to delete link:', error);
          }
        }}
      />

      <LinkTicketModal
        open={isEditLinkModalOpen}
        onClose={() => {
          setIsEditLinkModalOpen(false);
          setEditingLink(null);
        }}
        primaryTicket={{
          number: viewingLinksTask?.code,
          title: viewingLinksTask?.title
        }}
        relationshipTypes={memoizedRelationshipTypes}
        currentTicketId={viewingLinksTask?.id}
        existingLinkedTickets={(() => {
          try {
            return viewingLinksTask?.linked_tickets ?
              Object.entries(viewingLinksTask.linked_tickets).flatMap(([relationshipType, relationshipData]) =>
                (relationshipData?.tickets || []).map(ticket => ({
                  ...ticket,
                  relationshipType
                })) || []
              ) : [];
          } catch (error) {
            console.error('Error processing linked tickets:', error);
            return [];
          }
        })()}
        searchTickets={searchTicketsCallback}
        editMode={true}
        initialData={memoizedEditLinkInitialData}
        onLinkTickets={async (linkData) => {
          try {
            const relationshipId = editingLink?.ticket?.relationship_id;
            if (!relationshipId) {
              toast.error(t('links.errors.relationshipIdRequired'));
              return;
            }

            await dispatch(updateLinkTickets({
              relationshipId,
              relationshipType: linkData.relationshipType,
              related_ticket_id: linkData.targetTicketId,
              target_ticket: linkData.target_ticket
            })).unwrap();

            // Modal will close automatically, then refresh data
            requestAnimationFrame(() => {
              if (selectedProject !== FILTER_VALUES.ALL) {
                dispatch(fetchTicketsByProject({ projectId: selectedProject, filters: getCurrentFilters() }));
              }
            });

            setEditingLink(null);
          } catch (error) {
            console.error('Failed to update link:', error);
          }
        }}
      />

      <ProjectStatusModal
        open={isProjectStatusModalOpen}
        onClose={closeProjectStatusModal}
        onActivate={handleActivateProject}
        loading={activatingProject}
        projectStatus={projectStatusForModal}
        contentOverride={projectStatusModalContent}
      />
    </div>
  );
};

export default TaskDashboard;
